<template>
  <div class="snakegame-container maze-game-wrapper" style="touch-action: manipulation; user-select: none; -webkit-touch-callout: none; -webkit-user-select: none;">
    <!-- 小螢幕提示遮罩 -->
    <div v-if="showSmallScreenWarning && !isWarningDismissed" class="small-screen-warning">
      <div class="warning-content">
        <svg-icon icon-class="info" class="warning-icon" />
        <p class="warning-text">{{ $t('Mazegame.smallScreenWarning') || '本遊戲為桌面版介面，建議於電腦平板使用，且最低螢幕寬度需求為 1200px*800px' }}</p>
        <button class="warning-close" @click="dismissWarning">
          <svg-icon icon-class="close" />
        </button>
      </div>
    </div>

    <!-- 遊戲區域 -->
    <div class="game-area" :class="{ 'no-shadow': beforeGame }">
      <!-- 1. 遊戲標題（橙色，橫跨整個頂部） -->
      <div class="game-header">
        <div class="game-topic">{{ gameConfig?.topic || 'Maze(Demo)' }}</div>
      </div>

      <!-- 2. 左上角：題目進度 -->
      <div class="timer">
        {{ currentQuestionIndex + 1 }}/{{ gameConfig?.questionList?.length || 0 }}
        <button class="start-button" @click="togglePause">
          <svg-icon :icon-class="gameStarted ? (isPaused ? 'play' : 'pause') : 'play'" />
        </button>
      </div>

      <!-- 右上角：分數顯示（完全複製貪食蛇風格） -->
      <div v-if="gameStarted && !beforeGame" class="score-display-sm">
        <div class="score-value">{{ score }}</div>
      </div>

      <!-- 飛行分數效果（完全複製貪食蛇風格） -->
      <div
        v-for="fs in flyingScores"
        :key="fs.id"
        class="flying-score-effect"
        :style="{ left: fs.x + 'px', top: fs.y + 'px', color: fs.color }"
      >
        {{ fs.text }}
      </div>

      <!-- 3. 遊戲開始前彈窗 -->
      <div v-if="beforeGame" class="before-game-container">
        <!-- 正式活動模式：包含學生資訊輸入 -->
        <div v-if="isFromActId" class="actid-mode-wrapper">
          <div class="actid-mode-modal">
            <!-- 中間：遊戲資訊和播放按鈕 -->
            <div class="game-info-center">
              <div class="game-title">{{ gameConfig?.topic || 'Maze(Demo)' }}</div>
              <div class="game-info">
                <div class="game-time">{{ $t('Mazegame.totalQuestions') }}: {{ gameConfig?.questionList?.length || 0 }}</div>
              </div>
              <!-- 按鈕區 -->
              <div class="center-buttons">
                <button class="action-button play-button play-in-center" @click="showReadyGoCountdown()">
                  <svg-icon icon-class="play" class="play-icon" />
                </button>
              </div>
            </div>

            <!-- 右側：學生資訊輸入（支援 HiTeach 名單模式） -->
            <div class="student-info-right">
              <h4 class="student-info-title">{{ $t('gameActManager.enterStudentInfo') }}</h4>

              <!-- HiTeach 名單下拉選單模式 -->
              <template v-if="classRosterSource === 'HT' && classRoster.length > 0">
                <div class="form-group">
                  <select
                    @change="handleRosterSelection"
                    class="student-input student-select"
                  >
                    <option value="">{{ $t('gameActManager.selectStudent') }}</option>
                    <option
                      v-for="member in classRoster"
                      :key="`${member.memberName}-${member.seatID}`"
                      :value="`${member.memberName}-${member.seatID}`"
                    >
                      {{ member.seatID }} - {{ member.memberName }}
                    </option>
                  </select>
                  <span v-if="studentInfoErrors.name" class="error-text">{{ studentInfoErrors.name }}</span>
                </div>

                <!-- 顯示已選擇的資訊（唯讀） -->
                <div v-if="studentInfo.name" class="selected-info">
                  <div class="info-item">
                    <span class="info-label">{{ $t('gameActManager.seatNumber') }}:</span>
                    <span class="info-value">{{ studentInfo.seatNumber }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">{{ $t('gameActManager.studentName') }}:</span>
                    <span class="info-value">{{ studentInfo.name }}</span>
                  </div>
                </div>
              </template>

              <!-- 手動輸入模式（預設） -->
              <template v-else>
                <div class="form-group">
                  <input
                    v-model="studentInfo.seatNumber"
                    type="text"
                    :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                    maxlength="3"
                    class="student-input"
                  />
                  <span v-if="studentInfoErrors.seatNumber" class="error-text">{{ studentInfoErrors.seatNumber }}</span>
                </div>
                <div class="form-group">
                  <input
                    v-model="studentInfo.name"
                    type="text"
                    :placeholder="$t('gameActManager.studentNamePlaceholder')"
                    maxlength="20"
                    class="student-input"
                  />
                  <span v-if="studentInfoErrors.name" class="error-text">{{ studentInfoErrors.name }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Demo/預覽模式：原有佈局 -->
        <div v-else class="before-game-modal">
          <div class="game-title">{{ gameConfig?.topic || 'Maze(Demo)' }}</div>
          <div class="game-info">
            <div class="game-time">{{ $t('Mazegame.totalQuestions') }}: {{ gameConfig?.questionList?.length || 0 }}</div>
          </div>
          <!-- 推送後顯示短網址 -->
          <div v-if="pushedShortUrl" class="url-wrap url-wrap-centered">
              <span class="url-title">URL：</span><span style="color: #2B8EE0; font-size: 14px; font-weight: bold;">{{ pushedShortUrlHost }}</span><span class="short-url">{{ pushedShortCode }}</span>
              <span class="copy-btn" @click="copyPushedShortUrl">
                  <svg-icon icon-class="copy" />
              </span>
              <span class="qrcode-btn" @click="showPushedQRCode">
                  <svg-icon icon-class="qrcode-new" />
              </span>
              <span v-if="isWebView && !isPreviewMode" class="repush-btn" @click="repush" :title="$t('Game.repush') || '重新推送'">
                  <svg-icon icon-class="upload" class="repush-icon" />
              </span>
          </div>
          <!-- QR Code overlay -->
          <div v-if="showQRCodeOverlay" class="qrcode-overlay" @click.self="closeQRCodeOverlay">
              <div class="qrcode-modal">
                  <button class="qrcode-close-btn" @click="closeQRCodeOverlay">×</button>
                  <canvas ref="qrcodeCanvas" class="qrcode-canvas"></canvas>
                  <div style="margin-top: 10px; background-color: #fff9c4; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px; border-radius: 5px; white-space: nowrap; font-weight: bold;">
                      <span style="color: #333; font-size: 14px;">URL：</span><span style="color: #2B8EE0; font-size: 14px; font-weight: bold;">{{ pushedShortUrlHost }}</span><span style="color: #000; font-size: 26px; background-color: #fff; padding: 4px 6px; border-radius: 3px; font-weight: bold;">{{ pushedShortCode }}</span>
                  </div>
              </div>
          </div>

          <!-- 按鈕區 -->
          <div class="game-buttons">
            <button class="action-button play-button" @click="showReadyGoCountdown()">
              <svg-icon icon-class="play" class="play-icon" />
            </button>
            <button v-if="!isPreviewMode" class="action-button setting-button" @click="goToSettings()">
              <svg-icon icon-class="setting-game" class="setting-icon" />
            </button>
            <button v-if="isWebView && !isPreviewMode && isLoggedIn && !pushedActivityId" class="action-button push-button"
                    @click="handleSendClick()">
              <svg-icon :icon-class="schoolVers ? 'cloud-push' : 'upload'" class="sendweb-icon" />
            </button>
            <!-- ✨ 新增：查看成績按鈕（只在 WebView 且已推送時顯示） -->
            <button v-if="isWebView && !isPreviewMode && pushedActivityId"
                    class="action-button view-scores-button"
                    @click="openScoresPanel"
                    :title="$t('HTwbActKitHome.viewScores')">
              <svg-icon icon-class="clipboard-list" class="scores-icon" />
            </button>
            <!-- 雲端管理按鈕：推送後顯示，點擊導向遊戲雲端管理 -->
            <button v-if="isWebView && !isPreviewMode && pushedActivityId"
                    class="action-button cloud-manage-button"
                    @click="openCloudManagement"
                    :title="$t('HTwbActKitHome.cloudGameManagement')">
              <svg-icon icon-class="cloud-home" class="cloud-manage-icon" />
            </button>
          </div>
        </div>
      </div>

      <!-- Ready, Go! 提示 -->
      <div v-if="showReadyGo" class="ready-go-container">
        <div class="ready-go-text">{{ readyGoText }}</div>
      </div>

      <!-- 預覽模式標籤（左下角） -->
      <div v-if="isPreviewMode" class="preview-badge-bottom-left">{{ $t('gameActManager.previewMode') }}</div>

      <!-- 題目顯示區（在標題下方，遊戲區域上方） -->
      <div v-if="!beforeGame" class="question-panel-top">
        <div class="question-text">
         {{ currentQuestion?.text || '(題目載入中...)' }}
        </div>
      </div>

      <!-- 遊戲主畫面 -->
      <div class="maze-game-container">

        <!-- 主遊戲區域 -->
        <div class="game-main">
          <!-- 等比縮放外層：提供實際佔位尺寸（scale 後寬高） -->
          <div class="scale-outer" :style="scaleOuterStyle">
            <!-- 等比縮放內層：套用 transform: scale(...) 到原生尺寸容器 -->
            <div class="scale-inner" :style="scaleInnerStyle">
              <div class="canvas-wrapper">
                <canvas
                  ref="mazeCanvas"
                  class="maze-canvas"
                ></canvas>

                <!-- 方向控制按鈕（環繞角色周圍） -->
                <div class="direction-controls-overlay" :style="controlsPosition">
                  <button class="control-btn control-up" @click="startPlayerMovement({ dx: 0, dy: -1 })">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 14l5-5 5 5z"/>
                    </svg>
                  </button>
                  <button class="control-btn control-left" @click="startPlayerMovement({ dx: -1, dy: 0 })">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 7l-5 5 5 5z"/>
                    </svg>
                  </button>
                  <button class="control-btn control-right" @click="startPlayerMovement({ dx: 1, dy: 0 })">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 7l5 5-5 5z"/>
                    </svg>
                  </button>
                  <button class="control-btn control-down" @click="startPlayerMovement({ dx: 0, dy: 1 })">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 反饋訊息 -->
          <transition name="feedback">
            <div v-if="feedbackMessage" :class="['feedback-message', feedbackType]">
              {{ feedbackMessage }}
            </div>
          </transition>
        </div>
      </div>

      <!-- 4. 遊戲暫停彈窗（完全複製貪食蛇風格） -->
      <div v-if="gameStarted && isPaused && !isGameOver" class="pause-game-container">
        <div class="pause-game-modal">
          <div class="pause-title">{{ $t('Mazegame.gamePaused') }}</div>
          <div class="pause-buttons">
            <button class="action-button continue-button" @click="resumeGame()">
              <svg-icon icon-class="play" class="play-icon" />
            </button>
            <button class="action-button restart-button" @click="restartGame()">
              <svg-icon icon-class="refresh" class="refresh-icon" />
            </button>
          </div>
        </div>
      </div>

      <!-- 5. 遊戲結束彈窗（完全複製貪食蛇風格） -->
      <div v-if="isGameOver" class="game-over-modal">
        <div class="maze-game-result-container game-result-container single-player-result" v-show="!showAnswerModal">
          <!-- 結果卡片（完全複製貪食蛇單人模式） -->
          <div class="result-card-wrap">
            <div class="result-card">
              <!-- 遊戲結束 Badge（卡片內部，完全複製貪食蛇） -->
              <div class="result-header">
                <div class="result-title maze-result-title">{{ $t('Mazegame.gameOver') }}</div>
              </div>

              <!-- 分數顯示 -->
              <div class="result-score">
                <div class="star-score">
                  <span class="score-number">{{ score }}</span>
                </div>
              </div>

              <!-- 結果詳情 -->
              <div class="result-details">
                <div class="detail-item">
                  {{ $t('Mazegame.accuracy') }}: {{ accuracy }}%
                </div>
                <div class="detail-item">
                  {{ $t('Mazegame.timeSpent') }}: {{ formatTime(timeSpent) }}
                </div>
                <div class="detail-item">
                  {{ $t('Mazegame.wrongAttempts') }}: {{ wrongAttempts }}
                </div>
                <!-- 排名資訊（僅在成績提交後有回傳時顯示） -->
                <div v-if="submitResult" class="rank-info">
                  <div class="detail-item">
                    {{ $t('Mazegame.currentRank') }}: {{ submitResult.rank }}
                  </div>
                  <div class="detail-item">
                    {{ $t('Mazegame.totalParticipants') }}: {{ submitResult.totalCount }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 答案彈窗（完全複製貪食蛇風格 + 捲動功能） -->
        <div v-if="showAnswerModal" class="answer-modal-mask">
          <div class="answer-modal-card">
            <div class="answer-modal-header">
              <span class="answer-modal-title">{{ $t('Mazegame.referenceAnswers') }}</span>
              <button class="answer-modal-close" @click="toggleAnswerModal">×</button>
            </div>

            <!-- 答案內容區域（可捲動） -->
            <div class="answer-modal-content">
              <!-- 每一題的答案 -->
              <div v-for="(question, qIndex) in gameConfig?.questionList" :key="'q-' + qIndex" class="answer-modal-question">
                <div class="answer-question-title">{{ qIndex + 1 }}. {{ question.text }}</div>
                <div class="answer-modal-section">
                  <div class="answer-label correct">✔</div>
                  <div class="answer-list">
                    <span v-for="(option, oIndex) in question.options.filter(o => o.isCorrect)"
                          :key="'c-' + qIndex + '-' + oIndex"
                          class="answer-item correct">{{ option.text }}</span>
                  </div>
                </div>
                <div class="answer-modal-section">
                  <div class="answer-label wrong">✘</div>
                  <div class="answer-list">
                    <span v-for="(option, oIndex) in question.options.filter(o => !o.isCorrect)"
                          :key="'w-' + qIndex + '-' + oIndex"
                          class="answer-item wrong">{{ option.text }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按鈕區（答案按鈕 + 重新開始） -->
        <div class="game-result-actions" v-show="!showAnswerModal">
          <button class="action-button answer-button" @click="toggleAnswerModal">
            <span class="answer-text">A</span>
          </button>
          <button class="action-button restart-button" @click="restartGame">
            <svg-icon icon-class="refresh" class="refresh-icon" />
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- ✨ 新增：成績查看面板 -->
  <ActivityScoresPanel
    v-if="showScoresPanel"
    :activity-id="pushedActivityId"
    :activity-name="pushedActivityName"
    :roster-source="classRosterSource || 'general'"
    @back="closeScoresPanel"
  />

  <!-- 推送確認彈窗 -->
  <PushConfirmModal
    v-model="showPushConfirm"
    :activity-name="gameConfig?.topic || ''"
    :message="!htStartClass ? $t('gameActManager.pushConfirmMessageNoClass') : ''"
    @confirm="executePush"
  />
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, watch, inject, nextTick } from 'vue';
import { Loading } from 'quasar';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@/components/SvgIcon.vue';
import ActivityScoresPanel from '@/GameActManager/components/ActivityScoresPanel.vue';
import PushConfirmModal from '@/GameActManager/components/PushConfirmModal.vue';
import gameActivityAPI from '@/api/gameActivityAPI';
import api from '@/api';
import jwt_decode from 'jwt-decode';
import { validateAndRedirectGame } from '@/utils/gameActivityRouter';
import { useGameMessageBridge } from '@/composable/useGameMessageBridge';
import { useGameActivityPush } from '@/composable/useGameActivityPush';
import { useHiTeachVersion } from '@/composable/useHiTeachVersion';
import { useSchoolVersion } from '@/composable/useSchoolVersion';
import QRCode from 'qrcode';

const router = useRouter();
const { t } = useI18n();
const $Message = inject('$Message');

// HiTeach 版本管理
const { htVersion, isNewPushFuncVersion, loadVersion } = useHiTeachVersion('HTMazeGamePlay');

// 校園版授權判斷
const { schoolVers } = useSchoolVersion();

// 使用遊戲訊息橋接器 Composable（處理 Init.GameHome 和使用者驗證）
useGameMessageBridge('HTMazeGamePlay');

// Canvas ref
const mazeCanvas = ref(null);
const scale = ref(1);
const baseCanvasW = ref(1000);
const baseCanvasH = ref(600);

const scaleInnerStyle = computed(() => ({
  width: baseCanvasW.value + 'px',
  height: baseCanvasH.value + 'px',
  transform: `scale(${scale.value})`,
  // 以中心點為縮放原點，確保等比縮放時位置不會往上「堆疊」
  transformOrigin: 'center center'
}));

const scaleOuterStyle = computed(() => ({
  width: baseCanvasW.value * scale.value + 'px',
  height: baseCanvasH.value * scale.value + 'px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

// ==================== 遊戲模式相關 ====================
const isPreviewMode = ref(false);
const isFromActId = ref(false); // 是否從 actId 進入（正式活動模式）

// 小螢幕警告相關
const windowWidth = ref(window.innerWidth);
const showSmallScreenWarning = computed(() => windowWidth.value < 1200);
const isWarningDismissed = ref(false);

// 遊戲模板切換相關
const hasGameSwitched = ref(false); // 是否已經切換過遊戲
const allowTemplateChange = ref(true); // 活動是否允許切換遊戲模板

// HiTeach 名單相關
const classRosterSource = ref(''); // 名單來源
const classRoster = ref([]); // 解析後的名單陣列
const useHiTeachRoster = ref(schoolVers.value); // 是否使用 HiTeach 名單（校園版預設勾選）

// 遊戲配置
const gameConfig = ref(null);

// 遊戲狀態
const beforeGame = ref(true); // 遊戲開始前
const showReadyGo = ref(false); // 顯示 Ready/Go 提示
const readyGoText = ref(''); // Ready/Go 文字
const gameStarted = ref(false); // 遊戲是否已開始
const currentQuestionIndex = ref(0);
const score = ref(0);
const wrongAttempts = ref(0);
const startTime = ref(null);
const timeSpent = ref(0);
const isPaused = ref(false);
const isGameOver = ref(false);
const feedbackMessage = ref('');
const feedbackType = ref(''); // 'success' | 'error'
const monsterMoveInterval = ref(null); // 怪物移動計時器
const playerMoveInterval = ref(null);  // 玩家慣性移動計時器
const currentMoveDir = ref(null);      // 當前玩家移動方向
const answeredBlocks = ref(new Set()); // 記錄已經回答過的答案方塊（防止重複扣分）
const showAnswerModal = ref(false); // 顯示答案彈窗
const collidedMonsters = ref(new Set()); // 記錄已經碰撞過的怪物（防止重複扣分）
const flyingScores = ref([]); // 飛行分數效果列表
const scoreSubmitted = ref(false); // 防止重複提交成績
const submitResult = ref(null); // 成績提交後的排名資訊

// 畫面縮放動畫狀態
const hasPlayedZoomAnimation = ref(false); // 是否已經播放過初始放大動畫
const zoomAnimating = ref(false);          // 當前是否正在進行縮放動畫

// 學生資訊
const studentInfo = reactive({
  name: '',
  seatNumber: ''
});

const studentInfoErrors = reactive({
  name: '',
  seatNumber: ''
});

// ==================== 通信工具類 (MessageBridge) ====================
const MessageBridge = {
  // 環境類型
  ENV: {
    WEBVIEW: 'webview',
    WEB: 'web'
  },
  // 當前環境
  currentEnv: 'webview',
  // 初始化
  init() {
    this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
    return this;
  },
  // 發送消息
  postMessage(data, origin = '*') {
    if (this.currentEnv === this.ENV.WEBVIEW) {
      window?.chrome?.webview.postMessage(data);
    } else {
      window.postMessage(data, origin);
    }
  },
  // 添加消息監聽器
  addEventListener(callback) {
    const wrappedCallback = (event) => callback(event);
    // 將包裝後的回調存儲起來，以便後續移除
    if (!this._listeners) {
      this._listeners = new WeakMap();
    }
    this._listeners.set(callback, wrappedCallback);

    if (this.currentEnv === this.ENV.WEBVIEW) {
      window?.chrome?.webview.addEventListener('message', wrappedCallback);
    } else {
      window.addEventListener('message', wrappedCallback);
    }
  },
  // 移除消息監聽器
  removeEventListener(callback) {
    if (!this._listeners) {
      return;
    }
    const wrappedCallback = this._listeners.get(callback);
    if (!wrappedCallback) {
      return;
    }

    if (this.currentEnv === this.ENV.WEBVIEW) {
      window?.chrome?.webview.removeEventListener('message', wrappedCallback);
    } else {
      window.removeEventListener('message', wrappedCallback);
    }
    this._listeners.delete(callback);
  }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// WebView 環境檢測
const isWebView = computed(() => {
  return messageBridge.currentEnv === MessageBridge.ENV.WEBVIEW;
});

// 推送按鈕冷卻狀態
const isPushing = ref(false);

// ✨ 成績查看面板相關狀態
const showScoresPanel = ref(false);
const pushedActivityId = ref('');  // 儲存最新推送的活動 ID
const pushedActivityName = ref(''); // 儲存活動名稱
const pushedShortUrl = ref(''); // 儲存最新推送的完整短網址（如 https://hiteach.cc/abc123）
const showQRCodeOverlay = ref(false);
const qrcodeCanvas = ref(null);

// 從完整短網址拆出 host 與 code（如 "https://hiteach.cc/" 與 "abc123"）
const pushedShortUrlHost = computed(() => {
    if (!pushedShortUrl.value) return '';
    const match = pushedShortUrl.value.match(/^(https?:\/\/[^/]+\/)/);
    return match ? match[1] : '';
});
const pushedShortCode = computed(() => {
    if (!pushedShortUrl.value || !pushedShortUrlHost.value) return pushedShortUrl.value;
    return pushedShortUrl.value.slice(pushedShortUrlHost.value.length);
});

// 推送確認彈窗狀態
const showPushConfirm = ref(false);

// HiTeach 上課狀態（由 Send.HTStartClass 更新）
const htStartClass = ref(true);
let htStartClassPending = null;

// 詢問 HT 目前是否開始課堂，等待 Send.HTStartClass 回應
const queryHTStartClass = () => {
  return new Promise((resolve) => {
    Loading.show();
    let settled = false;
    const done = (result) => {
      if (settled) return;
      settled = true;
      Loading.hide();
      resolve(result);
    };
    htStartClassPending = done;
    messageBridge.postMessage({ action: 'Get.HTStartClass' });
    setTimeout(() => done(true), 2000);
  });
};

// 監聽器引用（用於清理）
let messageHandlerRef = null;

// 短網址域名配置（已移至 useGameActivityPush composable 中）
// const shortURLHost = computed(() => {
//     return locale.value === 'zh-CN' ? 'hiteach.cn' : 'hiteach.cc';
// });

// 使用統一的遊戲活動推送 composable
const { createAndPushShortUrl, isLoggedIn } = useGameActivityPush({
    gameName: 'maze',
    gameType: 'mazegame',
    gameRoute: '/ht-mazegame-play',
    questionType: 'multiChoice',  // 統一使用 multiChoice
    messageBridge: messageBridge,
    htVersion: htVersion,
    serializeGameConfig: (config) => JSON.stringify({
        topic: config.topic,
        questionList: config.questionList
    })
});

// 音效（預先聲明，但不初始化）
const readySound = ref(null);
const goSound = ref(null);
const gameOverSound = ref(null);
const correctSound = ref(null);
const wrongSound = ref(null);
const backgroundMusic = ref(null);

// 迷宮資料
const maze = ref(null);
const answerBlocks = ref([]); // 答案方塊位置
const monsters = ref([]); // 怪物位置
const playerX = ref(0);
const playerY = ref(0);

// 動物圖片列表（從 Openclipart 資料夾）
const animalImages = [
  'animals_bear.png',
  'animals_cat1.png',
  'animals_cat2.png',
  'animals_cat3.png',
  'animals_chicken.png',
  'animals_cow.png',
  'animals_deer.png',
  'animals_duck.png',
  'animals_elephant.png',
  'animals_fox.png',
  'animals_frog.png',
  'animals_giraffe.png',
  'animals_goat.png',
  'animals_lion.png',
  'animals_monkey.png',
  'animals_mouse.png',
  'animals_owl.png',
  'animals_panda.png',
  'animals_penguin.png',
  'animals_pig.png',
  'animals_poodle.png',
  'animals_rabbit.png',
  'animals_redpanda.png',
  'animals_sheep.png',
  'animals_shibainu.png',
  'animals_tiger.png',
  'animals_wolf.png'
];

// 隨機選擇動物圖片（確保主角和怪物不同）
function getRandomAnimalImages() {
  const shuffled = [...animalImages].sort(() => Math.random() - 0.5);
  return {
    player: shuffled[0],
    monster: shuffled[1]
  };
}

// 當前選擇的動物
const selectedAnimals = ref(getRandomAnimalImages());

// 載入圖片
const playerImage = new Image();
const monsterImage = new Image();

// 動態載入選中的動物圖片
function loadAnimalImages() {
  const animals = getRandomAnimalImages();
  selectedAnimals.value = animals;
  playerImage.src = require(`@/assets/Openclipart/${animals.player}`);
  monsterImage.src = require(`@/assets/Openclipart/${animals.monster}`);
  console.log(`🎮 主角: ${animals.player}, 怪物: ${animals.monster}`);
}

// 初始載入
loadAnimalImages();

const player = reactive({
  get x() { return playerX.value; },
  set x(val) { playerX.value = val; },
  get y() { return playerY.value; },
  set y(val) { playerY.value = val; },
  sprite: playerImage // 動態選擇的動物角色
});

// Cell types
const CellType = {
  WALL: 0,
  PATH: 1,
  ANSWER_BLOCK: 2
};

// 當前題目
const currentQuestion = computed(() => {
  if (!gameConfig.value || !gameConfig.value.questionList) return null;
  return gameConfig.value.questionList[currentQuestionIndex.value];
});

// 正確率
const accuracy = computed(() => {
  const total = currentQuestionIndex.value + wrongAttempts.value;
  if (total === 0) return 100;
  return ((currentQuestionIndex.value / total) * 100).toFixed(1);
});

// 控制按鈕位置（使用 ref 儲存，手動更新）
const controlsPosition = ref({
  left: '0px',
  top: '0px',
  transform: 'translate(-50%, -50%)'
});

// 更新按鈕位置的函數
function updateControlsPosition() {
  if (!renderer) return;

  const cellSize = renderer.cellSize || 20;
  const offsetX = renderer.viewOffsetX || 0;
  const offsetY = renderer.viewOffsetY || 0;
  const playerPxX = playerX.value * cellSize + cellSize / 2 + offsetX;
  const playerPxY = playerY.value * cellSize + cellSize / 2 + offsetY;

  controlsPosition.value = {
    left: `${playerPxX}px`,
    top: `${playerPxY}px`,
    transform: 'translate(-50%, -50%)'
  };
}

// 監聽玩家位置變化
watch([playerX, playerY], () => {
  updateControlsPosition();
});

// 視窗縮放時更新等比縮放比例
function updateScale() {
  try {
    const vw = window.innerWidth || document.documentElement.clientWidth || 1200;

    // 頁面可捲動，僅以寬度約束縮放，高度自然展開
    const sideMargin = 60;
    const availW = Math.max(320, vw - sideMargin);

    const sW = availW / (baseCanvasW.value || 1);
    let s = Math.min(sW, 1);

    scale.value = Number.isFinite(s) && s > 0 ? s : 1;
  } catch (_) {
    scale.value = 1;
  }
}

// ==================== 預設迷宮生成器（5 種複雜迷宮隨機輪流）====================
class PresetMazeGenerator {
  constructor(width, height, optionCount = 4) {
    this.width = width;   // 40
    this.height = height; // 24
    this.grid = [];
    this.optionCount = Math.min(Math.max(optionCount, 2), 4);

    // 中心點和房間位置（固定位置確保答案區塊可見）
    this.cx = Math.floor(this.width / 2);   // 20
    this.cy = Math.floor(this.height / 2);  // 12

    // 固定的答案房間位置（四個角落，需要繞路才能到達）
    this.roomPositions = [
      { x: 6, y: 4, width: 7, height: 5 },       // 左上角
      { x: 34, y: 4, width: 7, height: 5 },      // 右上角
      { x: 34, y: 19, width: 7, height: 5 },     // 右下角
      { x: 6, y: 19, width: 7, height: 5 }       // 左下角
    ];

    // 根據選項數量選取房間
    this.rooms = this.roomPositions.slice(0, this.optionCount);

    // 追蹤當前使用的迷宮索引（用於避免連續重複）
    this.currentMazeIndex = PresetMazeGenerator.lastMazeIndex;
  }

  // 靜態變數追蹤上次使用的迷宮
  static lastMazeIndex = -1;

  generate() {
    // 隨機選擇一種迷宮（避免連續重複）
    let mazeIndex;
    do {
      mazeIndex = Math.floor(Math.random() * 5);
    } while (mazeIndex === PresetMazeGenerator.lastMazeIndex && PresetMazeGenerator.lastMazeIndex !== -1);
    PresetMazeGenerator.lastMazeIndex = mazeIndex;

    console.log(`🗺️ 使用預設迷宮 #${mazeIndex + 1}`);

    // 初始化全部為牆壁
    this.grid = Array(this.height).fill(null).map(() =>
      Array(this.width).fill(CellType.WALL)
    );

    // 根據索引生成不同的迷宮佈局
    switch (mazeIndex) {
      case 0: this.generateSpiralMaze(); break;      // 螺旋型
      case 1: this.generateLabyrinthMaze(); break;   // 迷宮型
      case 2: this.generateZigzagMaze(); break;      // 之字型
      case 3: this.generateChamberMaze(); break;     // 房間型
      case 4: this.generateSnakeMaze(); break;       // 蛇行型
    }

    // 添加隨機障礙牆（讓迷宮更複雜）
    this.addRandomObstacles();

    // 確保房間區域是通路
    this.ensureRoomPaths();
    // 確保中心起點暢通
    this.ensureCenterPath();
    // 確保所有房間都可到達
    this.ensureConnectivity();

    return this.grid;
  }

  // 輔助方法：在指定位置挖出通路
  carvePath(x, y) {
    if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
      this.grid[y][x] = CellType.PATH;
    }
  }

  // 輔助方法：挖出水平走廊
  carveHorizontal(y, x1, x2, width = 3) {
    const startX = Math.min(x1, x2);
    const endX = Math.max(x1, x2);
    for (let x = startX; x <= endX; x++) {
      for (let dy = -Math.floor(width / 2); dy <= Math.floor(width / 2); dy++) {
        this.carvePath(x, y + dy);
      }
    }
  }

  // 輔助方法：挖出垂直走廊
  carveVertical(x, y1, y2, width = 3) {
    const startY = Math.min(y1, y2);
    const endY = Math.max(y1, y2);
    for (let y = startY; y <= endY; y++) {
      for (let dx = -Math.floor(width / 2); dx <= Math.floor(width / 2); dx++) {
        this.carvePath(x + dx, y);
      }
    }
  }

  // 迷宮 1：多層螺旋型 - 需要繞很多圈才能到角落
  generateSpiralMaze() {
    const cx = this.cx, cy = this.cy;

    // 中心小區域（只有一個出口）
    this.carveHorizontal(cy, cx - 2, cx + 2, 2);
    this.carveVertical(cx, cy - 1, cy + 1, 2);

    // 第一圈 - 順時針螺旋
    this.carveVertical(cx + 3, cy, cy - 3, 2);
    this.carveHorizontal(cy - 3, cx + 3, cx - 5, 2);
    this.carveVertical(cx - 5, cy - 3, cy + 3, 2);
    this.carveHorizontal(cy + 3, cx - 5, cx + 6, 2);

    // 第二圈 - 繼續螺旋
    this.carveVertical(cx + 6, cy + 3, cy - 5, 2);
    this.carveHorizontal(cy - 5, cx + 6, cx - 8, 2);
    this.carveVertical(cx - 8, cy - 5, cy + 5, 2);
    this.carveHorizontal(cy + 5, cx - 8, cx + 9, 2);

    // 第三圈 - 最外圈
    this.carveVertical(cx + 9, cy + 5, cy - 7, 2);
    this.carveHorizontal(cy - 7, cx + 9, cx - 11, 2);
    this.carveVertical(cx - 11, cy - 7, cy + 7, 2);
    this.carveHorizontal(cy + 7, cx - 11, cx + 12, 2);

    // 從螺旋末端分叉到四個角落
    // 左上角（從左上區域繞進去）
    this.carveVertical(cx - 11, cy - 7, 6, 2);
    this.carveHorizontal(6, cx - 11, 8, 2);
    this.carveVertical(8, 4, 6, 2);
    this.carveHorizontal(4, 6, 8, 2);

    // 右上角（從右上區域繞進去）
    this.carveHorizontal(cy - 7, cx + 9, 32, 2);
    this.carveVertical(32, cy - 7, 6, 2);
    this.carveHorizontal(6, 32, 34, 2);
    this.carveVertical(34, 4, 6, 2);

    // 左下角（從左下區域繞進去）
    this.carveVertical(cx - 11, cy + 7, 17, 2);
    this.carveHorizontal(17, cx - 11, 8, 2);
    this.carveVertical(8, 17, 19, 2);
    this.carveHorizontal(19, 6, 8, 2);

    // 右下角（從右下區域繞進去）
    this.carveVertical(cx + 12, cy + 7, 17, 2);
    this.carveHorizontal(17, cx + 12, 32, 2);
    this.carveVertical(32, 17, 19, 2);
    this.carveHorizontal(19, 32, 34, 2);

    // 死胡同干擾
    this.carveVertical(cx, cy - 4, cy - 1, 2);
    this.carveVertical(cx, cy + 1, cy + 4, 2);
    this.carveHorizontal(cy, cx + 10, cx + 14, 2);
    this.carveHorizontal(cy, cx - 10, cx - 14, 2);
  }

  // 迷宮 2：複雜迷宮型 - 多條岔路和死胡同
  generateLabyrinthMaze() {
    const cx = this.cx, cy = this.cy;

    // 中心區域（小）
    this.carveHorizontal(cy, cx - 2, cx + 2, 2);
    this.carveVertical(cx, cy - 1, cy + 1, 2);

    // 左上區域 - 複雜曲折
    this.carveVertical(cx - 3, cy - 1, cy, 2);
    this.carveHorizontal(cy - 1, cx - 3, cx - 6, 2);
    this.carveVertical(cx - 6, cy - 4, cy - 1, 2);
    this.carveHorizontal(cy - 4, cx - 6, cx - 3, 2);  // 死胡同
    this.carveHorizontal(cy - 4, cx - 6, cx - 10, 2);
    this.carveVertical(cx - 10, cy - 4, cy - 7, 2);
    this.carveHorizontal(cy - 7, cx - 10, cx - 6, 2);  // 另一個死胡同
    this.carveHorizontal(cy - 7, cx - 10, 10, 2);
    this.carveVertical(10, cy - 7, 6, 2);
    this.carveHorizontal(6, 10, 6, 2);
    this.carveVertical(6, 4, 6, 2);

    // 右上區域 - 複雜曲折
    this.carveVertical(cx + 3, cy - 1, cy, 2);
    this.carveHorizontal(cy - 1, cx + 3, cx + 6, 2);
    this.carveVertical(cx + 6, cy - 4, cy - 1, 2);
    this.carveHorizontal(cy - 4, cx + 6, cx + 3, 2);  // 死胡同
    this.carveHorizontal(cy - 4, cx + 6, cx + 10, 2);
    this.carveVertical(cx + 10, cy - 4, cy - 7, 2);
    this.carveHorizontal(cy - 7, cx + 10, cx + 6, 2);  // 另一個死胡同
    this.carveHorizontal(cy - 7, cx + 10, 30, 2);
    this.carveVertical(30, cy - 7, 6, 2);
    this.carveHorizontal(6, 30, 34, 2);
    this.carveVertical(34, 4, 6, 2);

    // 左下區域 - 複雜曲折
    this.carveVertical(cx - 3, cy, cy + 1, 2);
    this.carveHorizontal(cy + 1, cx - 3, cx - 6, 2);
    this.carveVertical(cx - 6, cy + 1, cy + 4, 2);
    this.carveHorizontal(cy + 4, cx - 6, cx - 3, 2);  // 死胡同
    this.carveHorizontal(cy + 4, cx - 6, cx - 10, 2);
    this.carveVertical(cx - 10, cy + 4, cy + 7, 2);
    this.carveHorizontal(cy + 7, cx - 10, cx - 6, 2);  // 另一個死胡同
    this.carveHorizontal(cy + 7, cx - 10, 10, 2);
    this.carveVertical(10, cy + 7, 17, 2);
    this.carveHorizontal(17, 10, 6, 2);
    this.carveVertical(6, 17, 19, 2);

    // 右下區域 - 複雜曲折
    this.carveVertical(cx + 3, cy, cy + 1, 2);
    this.carveHorizontal(cy + 1, cx + 3, cx + 6, 2);
    this.carveVertical(cx + 6, cy + 1, cy + 4, 2);
    this.carveHorizontal(cy + 4, cx + 6, cx + 3, 2);  // 死胡同
    this.carveHorizontal(cy + 4, cx + 6, cx + 10, 2);
    this.carveVertical(cx + 10, cy + 4, cy + 7, 2);
    this.carveHorizontal(cy + 7, cx + 10, cx + 6, 2);  // 另一個死胡同
    this.carveHorizontal(cy + 7, cx + 10, 30, 2);
    this.carveVertical(30, cy + 7, 17, 2);
    this.carveHorizontal(17, 30, 34, 2);
    this.carveVertical(34, 17, 19, 2);

    // 中間區域的干擾死胡同
    this.carveVertical(15, cy - 3, cy + 3, 2);
    this.carveVertical(25, cy - 3, cy + 3, 2);
  }

  // 迷宮 3：多層之字型 - 需要來回很多次
  generateZigzagMaze() {
    const cx = this.cx, cy = this.cy;

    // 中心區域（小）
    this.carveHorizontal(cy, cx - 2, cx + 2, 2);
    this.carveVertical(cx, cy - 1, cy + 1, 2);

    // 左上角路徑 - 多層Z字
    this.carveVertical(cx - 3, cy - 1, cy, 2);
    this.carveHorizontal(cy - 1, cx - 3, cx - 7, 2);
    this.carveVertical(cx - 7, cy - 1, cy - 4, 2);
    this.carveHorizontal(cy - 4, cx - 7, cx - 3, 2);
    this.carveVertical(cx - 3, cy - 4, cy - 6, 2);
    this.carveHorizontal(cy - 6, cx - 3, cx - 10, 2);
    this.carveVertical(cx - 10, cy - 6, cy - 8, 2);
    this.carveHorizontal(cy - 8, cx - 10, 8, 2);
    this.carveVertical(8, cy - 8, 5, 2);
    this.carveHorizontal(5, 8, 6, 2);
    this.carveVertical(6, 4, 5, 2);

    // 右上角路徑 - 多層Z字
    this.carveVertical(cx + 3, cy - 1, cy, 2);
    this.carveHorizontal(cy - 1, cx + 3, cx + 7, 2);
    this.carveVertical(cx + 7, cy - 1, cy - 4, 2);
    this.carveHorizontal(cy - 4, cx + 7, cx + 3, 2);
    this.carveVertical(cx + 3, cy - 4, cy - 6, 2);
    this.carveHorizontal(cy - 6, cx + 3, cx + 10, 2);
    this.carveVertical(cx + 10, cy - 6, cy - 8, 2);
    this.carveHorizontal(cy - 8, cx + 10, 32, 2);
    this.carveVertical(32, cy - 8, 5, 2);
    this.carveHorizontal(5, 32, 34, 2);
    this.carveVertical(34, 4, 5, 2);

    // 左下角路徑 - 多層Z字
    this.carveVertical(cx - 3, cy, cy + 1, 2);
    this.carveHorizontal(cy + 1, cx - 3, cx - 7, 2);
    this.carveVertical(cx - 7, cy + 1, cy + 4, 2);
    this.carveHorizontal(cy + 4, cx - 7, cx - 3, 2);
    this.carveVertical(cx - 3, cy + 4, cy + 6, 2);
    this.carveHorizontal(cy + 6, cx - 3, cx - 10, 2);
    this.carveVertical(cx - 10, cy + 6, cy + 8, 2);
    this.carveHorizontal(cy + 8, cx - 10, 8, 2);
    this.carveVertical(8, cy + 8, 18, 2);
    this.carveHorizontal(18, 8, 6, 2);
    this.carveVertical(6, 18, 19, 2);

    // 右下角路徑 - 多層Z字
    this.carveVertical(cx + 3, cy, cy + 1, 2);
    this.carveHorizontal(cy + 1, cx + 3, cx + 7, 2);
    this.carveVertical(cx + 7, cy + 1, cy + 4, 2);
    this.carveHorizontal(cy + 4, cx + 7, cx + 3, 2);
    this.carveVertical(cx + 3, cy + 4, cy + 6, 2);
    this.carveHorizontal(cy + 6, cx + 3, cx + 10, 2);
    this.carveVertical(cx + 10, cy + 6, cy + 8, 2);
    this.carveHorizontal(cy + 8, cx + 10, 32, 2);
    this.carveVertical(32, cy + 8, 18, 2);
    this.carveHorizontal(18, 32, 34, 2);
    this.carveVertical(34, 18, 19, 2);

    // 死胡同干擾
    this.carveVertical(cx, cy - 3, cy - 1, 2);
    this.carveVertical(cx, cy + 1, cy + 3, 2);
    this.carveHorizontal(cy - 3, cx - 5, cx + 5, 2);
    this.carveHorizontal(cy + 3, cx - 5, cx + 5, 2);
  }

  // 迷宮 4：雙環迴廊型 - 內外兩層環
  generateChamberMaze() {
    const cx = this.cx, cy = this.cy;

    // 中心（很小）
    this.carveHorizontal(cy, cx - 1, cx + 1, 2);
    this.carveVertical(cx, cy - 1, cy + 1, 2);

    // 內環（圍繞中心）
    this.carveHorizontal(cy - 3, cx - 4, cx + 4, 2);
    this.carveHorizontal(cy + 3, cx - 4, cx + 4, 2);
    this.carveVertical(cx - 4, cy - 3, cy + 3, 2);
    this.carveVertical(cx + 4, cy - 3, cy + 3, 2);

    // 中心到內環的單一出口
    this.carveVertical(cx, cy + 1, cy + 3, 2);

    // 外環（連接到角落）
    this.carveHorizontal(cy - 6, cx - 8, cx + 8, 2);
    this.carveHorizontal(cy + 6, cx - 8, cx + 8, 2);
    this.carveVertical(cx - 8, cy - 6, cy + 6, 2);
    this.carveVertical(cx + 8, cy - 6, cy + 6, 2);

    // 內環到外環的連接（只有一個出口）
    this.carveHorizontal(cy + 3, cx + 4, cx + 8, 2);

    // 從外環到四個角落
    // 左上角
    this.carveVertical(cx - 8, cy - 6, cy - 8, 2);
    this.carveHorizontal(cy - 8, cx - 8, 10, 2);
    this.carveVertical(10, cy - 8, 6, 2);
    this.carveHorizontal(6, 10, 6, 2);
    this.carveVertical(6, 4, 6, 2);

    // 右上角
    this.carveVertical(cx + 8, cy - 6, cy - 8, 2);
    this.carveHorizontal(cy - 8, cx + 8, 30, 2);
    this.carveVertical(30, cy - 8, 6, 2);
    this.carveHorizontal(6, 30, 34, 2);
    this.carveVertical(34, 4, 6, 2);

    // 左下角
    this.carveVertical(cx - 8, cy + 6, cy + 8, 2);
    this.carveHorizontal(cy + 8, cx - 8, 10, 2);
    this.carveVertical(10, cy + 8, 17, 2);
    this.carveHorizontal(17, 10, 6, 2);
    this.carveVertical(6, 17, 19, 2);

    // 右下角
    this.carveVertical(cx + 8, cy + 6, cy + 8, 2);
    this.carveHorizontal(cy + 8, cx + 8, 30, 2);
    this.carveVertical(30, cy + 8, 17, 2);
    this.carveHorizontal(17, 30, 34, 2);
    this.carveVertical(34, 17, 19, 2);

    // 死胡同
    this.carveHorizontal(cy - 3, cx - 4, cx - 7, 2);
    this.carveHorizontal(cy + 3, cx - 4, cx - 7, 2);
    this.carveVertical(cx, cy - 3, cy - 5, 2);
  }

  // 迷宮 5：複雜蛇行型 - 極度曲折
  generateSnakeMaze() {
    const cx = this.cx, cy = this.cy;

    // 中心（小）
    this.carveHorizontal(cy, cx - 2, cx + 2, 2);
    this.carveVertical(cx, cy - 1, cy + 1, 2);

    // 左上角的蛇行路徑
    this.carveVertical(cx - 3, cy - 1, cy, 2);
    this.carveHorizontal(cy - 1, cx - 3, cx - 5, 2);
    this.carveVertical(cx - 5, cy - 1, cy - 3, 2);
    this.carveHorizontal(cy - 3, cx - 5, cx - 8, 2);
    this.carveVertical(cx - 8, cy - 3, cy - 5, 2);
    this.carveHorizontal(cy - 5, cx - 8, cx - 5, 2);
    this.carveVertical(cx - 5, cy - 5, cy - 7, 2);
    this.carveHorizontal(cy - 7, cx - 5, cx - 11, 2);
    this.carveVertical(cx - 11, cy - 7, 6, 2);
    this.carveHorizontal(6, cx - 11, 8, 2);
    this.carveVertical(8, 4, 6, 2);
    this.carveHorizontal(4, 6, 8, 2);

    // 右上角的蛇行路徑
    this.carveVertical(cx + 3, cy - 1, cy, 2);
    this.carveHorizontal(cy - 1, cx + 3, cx + 5, 2);
    this.carveVertical(cx + 5, cy - 1, cy - 3, 2);
    this.carveHorizontal(cy - 3, cx + 5, cx + 8, 2);
    this.carveVertical(cx + 8, cy - 3, cy - 5, 2);
    this.carveHorizontal(cy - 5, cx + 8, cx + 5, 2);
    this.carveVertical(cx + 5, cy - 5, cy - 7, 2);
    this.carveHorizontal(cy - 7, cx + 5, cx + 11, 2);
    this.carveVertical(cx + 11, cy - 7, 6, 2);
    this.carveHorizontal(6, cx + 11, 32, 2);
    this.carveVertical(32, 4, 6, 2);
    this.carveHorizontal(4, 32, 34, 2);

    // 左下角的蛇行路徑
    this.carveVertical(cx - 3, cy, cy + 1, 2);
    this.carveHorizontal(cy + 1, cx - 3, cx - 5, 2);
    this.carveVertical(cx - 5, cy + 1, cy + 3, 2);
    this.carveHorizontal(cy + 3, cx - 5, cx - 8, 2);
    this.carveVertical(cx - 8, cy + 3, cy + 5, 2);
    this.carveHorizontal(cy + 5, cx - 8, cx - 5, 2);
    this.carveVertical(cx - 5, cy + 5, cy + 7, 2);
    this.carveHorizontal(cy + 7, cx - 5, cx - 11, 2);
    this.carveVertical(cx - 11, cy + 7, 17, 2);
    this.carveHorizontal(17, cx - 11, 8, 2);
    this.carveVertical(8, 17, 19, 2);
    this.carveHorizontal(19, 6, 8, 2);

    // 右下角的蛇行路徑
    this.carveVertical(cx + 3, cy, cy + 1, 2);
    this.carveHorizontal(cy + 1, cx + 3, cx + 5, 2);
    this.carveVertical(cx + 5, cy + 1, cy + 3, 2);
    this.carveHorizontal(cy + 3, cx + 5, cx + 8, 2);
    this.carveVertical(cx + 8, cy + 3, cy + 5, 2);
    this.carveHorizontal(cy + 5, cx + 8, cx + 5, 2);
    this.carveVertical(cx + 5, cy + 5, cy + 7, 2);
    this.carveHorizontal(cy + 7, cx + 5, cx + 11, 2);
    this.carveVertical(cx + 11, cy + 7, 17, 2);
    this.carveHorizontal(17, cx + 11, 32, 2);
    this.carveVertical(32, 17, 19, 2);
    this.carveHorizontal(19, 32, 34, 2);

    // 死胡同干擾
    this.carveVertical(cx, cy - 3, cy - 1, 2);
    this.carveVertical(cx, cy + 1, cy + 3, 2);
    this.carveHorizontal(cy, cx - 6, cx - 3, 2);
    this.carveHorizontal(cy, cx + 3, cx + 6, 2);
  }

  // 添加隨機障礙牆（讓迷宮更複雜）
  addRandomObstacles() {
    const cx = this.cx;
    const cy = this.cy;
    const obstacleCount = 25 + Math.floor(Math.random() * 15); // 25-40 個障礙

    for (let i = 0; i < obstacleCount; i++) {
      const obstacleType = Math.floor(Math.random() * 5);

      if (obstacleType === 0) {
        // 短橫牆（3-6 格寬）
        const length = 3 + Math.floor(Math.random() * 4);
        const x = Math.floor(Math.random() * (this.width - length - 4)) + 2;
        const y = Math.floor(Math.random() * (this.height - 4)) + 2;
        for (let dx = 0; dx < length; dx++) {
          if (this.isSafeToPlaceWall(x + dx, y, cx, cy)) {
            this.grid[y][x + dx] = CellType.WALL;
          }
        }
      } else if (obstacleType === 1) {
        // 短豎牆（3-6 格高）
        const length = 3 + Math.floor(Math.random() * 4);
        const x = Math.floor(Math.random() * (this.width - 4)) + 2;
        const y = Math.floor(Math.random() * (this.height - length - 4)) + 2;
        for (let dy = 0; dy < length; dy++) {
          if (this.isSafeToPlaceWall(x, y + dy, cx, cy)) {
            this.grid[y + dy][x] = CellType.WALL;
          }
        }
      } else if (obstacleType === 2) {
        // L 型牆
        const size = 3 + Math.floor(Math.random() * 3);
        const x = Math.floor(Math.random() * (this.width - size - 4)) + 2;
        const y = Math.floor(Math.random() * (this.height - size - 4)) + 2;
        const direction = Math.floor(Math.random() * 4);
        for (let d = 0; d < size; d++) {
          if (direction === 0) {
            if (this.isSafeToPlaceWall(x + d, y, cx, cy)) this.grid[y][x + d] = CellType.WALL;
            if (this.isSafeToPlaceWall(x, y + d, cx, cy)) this.grid[y + d][x] = CellType.WALL;
          } else if (direction === 1) {
            if (this.isSafeToPlaceWall(x + d, y, cx, cy)) this.grid[y][x + d] = CellType.WALL;
            if (this.isSafeToPlaceWall(x + size - 1, y + d, cx, cy)) this.grid[y + d][x + size - 1] = CellType.WALL;
          } else if (direction === 2) {
            if (this.isSafeToPlaceWall(x + d, y + size - 1, cx, cy)) this.grid[y + size - 1][x + d] = CellType.WALL;
            if (this.isSafeToPlaceWall(x, y + d, cx, cy)) this.grid[y + d][x] = CellType.WALL;
          } else {
            if (this.isSafeToPlaceWall(x + d, y + size - 1, cx, cy)) this.grid[y + size - 1][x + d] = CellType.WALL;
            if (this.isSafeToPlaceWall(x + size - 1, y + d, cx, cy)) this.grid[y + d][x + size - 1] = CellType.WALL;
          }
        }
      } else if (obstacleType === 3) {
        // T 型牆
        const size = 3 + Math.floor(Math.random() * 2);
        const x = Math.floor(Math.random() * (this.width - size - 4)) + 2;
        const y = Math.floor(Math.random() * (this.height - size - 4)) + 2;
        const direction = Math.floor(Math.random() * 4);
        const mid = Math.floor(size / 2);
        if (direction === 0) { // T 朝下
          for (let dx = 0; dx < size; dx++) {
            if (this.isSafeToPlaceWall(x + dx, y, cx, cy)) this.grid[y][x + dx] = CellType.WALL;
          }
          for (let dy = 0; dy < size; dy++) {
            if (this.isSafeToPlaceWall(x + mid, y + dy, cx, cy)) this.grid[y + dy][x + mid] = CellType.WALL;
          }
        } else if (direction === 1) { // T 朝上
          for (let dx = 0; dx < size; dx++) {
            if (this.isSafeToPlaceWall(x + dx, y + size - 1, cx, cy)) this.grid[y + size - 1][x + dx] = CellType.WALL;
          }
          for (let dy = 0; dy < size; dy++) {
            if (this.isSafeToPlaceWall(x + mid, y + dy, cx, cy)) this.grid[y + dy][x + mid] = CellType.WALL;
          }
        } else if (direction === 2) { // T 朝右
          for (let dy = 0; dy < size; dy++) {
            if (this.isSafeToPlaceWall(x, y + dy, cx, cy)) this.grid[y + dy][x] = CellType.WALL;
          }
          for (let dx = 0; dx < size; dx++) {
            if (this.isSafeToPlaceWall(x + dx, y + mid, cx, cy)) this.grid[y + mid][x + dx] = CellType.WALL;
          }
        } else { // T 朝左
          for (let dy = 0; dy < size; dy++) {
            if (this.isSafeToPlaceWall(x + size - 1, y + dy, cx, cy)) this.grid[y + dy][x + size - 1] = CellType.WALL;
          }
          for (let dx = 0; dx < size; dx++) {
            if (this.isSafeToPlaceWall(x + dx, y + mid, cx, cy)) this.grid[y + mid][x + dx] = CellType.WALL;
          }
        }
      } else {
        // 方塊障礙（2x2 或 3x3）
        const size = 2 + Math.floor(Math.random() * 2);
        const x = Math.floor(Math.random() * (this.width - size - 4)) + 2;
        const y = Math.floor(Math.random() * (this.height - size - 4)) + 2;
        for (let dy = 0; dy < size; dy++) {
          for (let dx = 0; dx < size; dx++) {
            if (this.isSafeToPlaceWall(x + dx, y + dy, cx, cy)) {
              this.grid[y + dy][x + dx] = CellType.WALL;
            }
          }
        }
      }
    }
  }

  // 檢查是否可以安全放置牆壁
  isSafeToPlaceWall(x, y, cx, cy) {
    // 邊界檢查
    if (x < 1 || x >= this.width - 1 || y < 1 || y >= this.height - 1) {
      return false;
    }

    // 已經是牆壁
    if (this.grid[y][x] === CellType.WALL) {
      return false;
    }

    // 保護中心點周圍 5x5 區域（玩家起點）
    if (Math.abs(x - cx) <= 2 && Math.abs(y - cy) <= 2) {
      return false;
    }

    // 保護房間區域（答案區塊周圍）
    for (const room of this.rooms) {
      const halfW = Math.floor(room.width / 2) + 1;
      const halfH = Math.floor(room.height / 2) + 1;
      if (x >= room.x - halfW && x <= room.x + halfW &&
          y >= room.y - halfH && y <= room.y + halfH) {
        return false;
      }
    }

    // 檢查周圍的牆壁數量，避免完全封閉
    let wallCount = 0;
    const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
    for (const [dx, dy] of directions) {
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 0 && nx < this.width && ny >= 0 && ny < this.height) {
        if (this.grid[ny][nx] === CellType.WALL) {
          wallCount++;
        }
      }
    }

    // 如果周圍已經有 3 個牆壁，不再添加
    return wallCount < 3;
  }

  // 確保房間區域是通路
  ensureRoomPaths() {
    for (const room of this.rooms) {
      const startX = Math.max(1, room.x - Math.floor(room.width / 2));
      const startY = Math.max(1, room.y - Math.floor(room.height / 2));
      const endX = Math.min(this.width - 2, startX + room.width);
      const endY = Math.min(this.height - 2, startY + room.height);

      for (let y = startY; y < endY; y++) {
        for (let x = startX; x < endX; x++) {
          this.carvePath(x, y);
        }
      }
    }
  }

  // 確保中心起點暢通
  ensureCenterPath() {
    // 挖出中心 5x5 區域
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        this.carvePath(this.cx + dx, this.cy + dy);
      }
    }
  }

  // 確保所有房間都可以從中心到達（使用 BFS 檢查，只在必要時補路）
  ensureConnectivity() {
    // 檢查每個房間是否可從中心到達
    for (const room of this.rooms) {
      if (!this.canReach(this.cx, this.cy, room.x, room.y)) {
        // 如果無法到達，使用曲折路徑連接（不是直線）
        // 先向房間方向走一段，再轉彎
        const midX = Math.floor((this.cx + room.x) / 2);
        const midY = Math.floor((this.cy + room.y) / 2);
        this.carveVertical(this.cx, Math.min(this.cy, midY), Math.max(this.cy, midY), 2);
        this.carveHorizontal(midY, Math.min(this.cx, midX), Math.max(this.cx, midX), 2);
        this.carveVertical(midX, Math.min(midY, room.y), Math.max(midY, room.y), 2);
        this.carveHorizontal(room.y, Math.min(midX, room.x), Math.max(midX, room.x), 2);
      }
    }
  }

  // BFS 檢查是否可以從起點到達終點
  canReach(startX, startY, endX, endY) {
    const visited = new Set();
    const queue = [[startX, startY]];
    const key = (x, y) => `${x},${y}`;

    while (queue.length > 0) {
      const [x, y] = queue.shift();
      if (x === endX && y === endY) return true;

      const k = key(x, y);
      if (visited.has(k)) continue;
      visited.add(k);

      // 檢查四個方向
      const dirs = [[0, 1], [0, -1], [1, 0], [-1, 0]];
      for (const [dx, dy] of dirs) {
        const nx = x + dx, ny = y + dy;
        if (nx >= 0 && nx < this.width && ny >= 0 && ny < this.height &&
            this.grid[ny][nx] !== CellType.WALL && !visited.has(key(nx, ny))) {
          queue.push([nx, ny]);
        }
      }
    }
    return false;
  }

  // 放置答案方塊（與原版相同介面）
  placeAnswerBlocks(options) {
    const blocks = [];
    const actualOptionCount = Math.min(options.length, this.rooms.length);

    for (let i = 0; i < actualOptionCount; i++) {
      const room = this.rooms[i];
      blocks.push({
        x: room.x,
        y: room.y,
        width: room.width,
        height: room.height,
        optionIndex: i,
        isCorrect: options[i].isCorrect,
        optionText: options[i].text
      });
      this.grid[room.y][room.x] = CellType.ANSWER_BLOCK;
    }
    return blocks;
  }

  getCenterPosition() {
    return { x: this.cx, y: this.cy };
  }

  // 生成怪物位置
  generateMonsters(count = 3) {
    const monsters = [];

    // 怪物生成在走廊上（避開中心和房間）
    const possiblePositions = [];
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.grid[y][x] !== CellType.PATH) continue;

        // 避開中心區域
        if (Math.abs(x - this.cx) <= 4 && Math.abs(y - this.cy) <= 4) continue;

        // 避開房間區域
        let nearRoom = false;
        for (const room of this.rooms) {
          if (Math.abs(x - room.x) <= 4 && Math.abs(y - room.y) <= 3) {
            nearRoom = true;
            break;
          }
        }
        if (nearRoom) continue;

        possiblePositions.push({ x, y });
      }
    }

    // 隨機選擇怪物位置
    for (let i = 0; i < count && possiblePositions.length > 0; i++) {
      const idx = Math.floor(Math.random() * possiblePositions.length);
      const pos = possiblePositions.splice(idx, 1)[0];
      monsters.push({
        x: pos.x,
        y: pos.y,
        sprite: monsterImage,
        id: `monster_${i}`
      });
    }

    return monsters;
  }
}

// ==================== Canvas 渲染器（Wordwall 風格）====================
class MazeRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.baseCellSize = 20;   // 基礎單元格大小（未縮放）
    this.cellSize = 20;       // 目前繪圖用單元格大小（含縮放）
    this.zoom = 1;            // 畫面縮放倍率（1~1.5）
    this.canvasWidth = 0;     // Canvas CSS 寬度
    this.canvasHeight = 0;    // Canvas CSS 高度
    this.viewOffsetX = 0;     // 相機 X 偏移（世界 -> 畫面）
    this.viewOffsetY = 0;     // 相機 Y 偏移

    // 顏色主題（Wordwall 太空風格）
    this.colors = {
      background: '#1a0d33',  // 深紫太空背景
      wall: '#3d2463',        // 紫色牆壁（帶橙邊框）
      wallBorder: '#ff9500',  // 橙色邊框
      path: '#2d1b4e',        // 深紫路徑
      answerBlock: '#5a3d8f', // 深紫答案底色
      answerGlow: '#b388ff',  // 紫色光暈
      answerText: '#ffffff',  // 白色文字
      playerGlow: '#00d4ff',  // 青色光暈
      correctHighlight: '#27ae60',
      wrongHighlight: '#e74c3c'
    };
  }

  setupCanvas(mazeWidth, mazeHeight) {
    // 以容器可用寬度為基準，允許高度自然展開（頁面可捲動）
    const containerWidth = Math.min(1200, window.innerWidth) - 40;

    // 計算基礎單元格大小（不含 zoom）
    this.baseCellSize = Math.floor(containerWidth / mazeWidth);

    // 初始以 zoom = 1 設定畫布大小
    const canvasWidth = mazeWidth * this.baseCellSize;
    const canvasHeight = mazeHeight * this.baseCellSize;

    // 設定 Canvas
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = canvasWidth * dpr;
    this.canvas.height = canvasHeight * dpr;
    this.canvas.style.width = `${canvasWidth}px`;
    this.canvas.style.height = `${canvasHeight}px`;

    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;

    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  render(mazeGrid, playerPos, blocks, monsters = []) {
    if (!mazeGrid || !mazeGrid.length) return;

    const mazeHeight = mazeGrid.length;
    const mazeWidth = mazeGrid[0].length;

    // 根據 zoom 計算實際 cell 大小
    this.cellSize = this.baseCellSize * (this.zoom || 1);
    const cellSize = this.cellSize;

    const worldWidth = mazeWidth * cellSize;
    const worldHeight = mazeHeight * cellSize;

    const canvasWidth = this.canvasWidth || (this.canvas.width / (window.devicePixelRatio || 1));
    const canvasHeight = this.canvasHeight || (this.canvas.height / (window.devicePixelRatio || 1));

    // 玩家在世界座標中的像素位置
    const playerPxX = playerPos.x * cellSize + cellSize / 2;
    const playerPxY = playerPos.y * cellSize + cellSize / 2;

    // 計算相機偏移：盡量讓玩家在畫面中央
    let offsetX = canvasWidth / 2 - playerPxX;
    let offsetY = canvasHeight / 2 - playerPxY;

    // 邊界夾住，避免鏡頭超出迷宮範圍
    offsetX = Math.min(0, Math.max(canvasWidth - worldWidth, offsetX));
    offsetY = Math.min(0, Math.max(canvasHeight - worldHeight, offsetY));

    this.viewOffsetX = offsetX;
    this.viewOffsetY = offsetY;

    this.ctx.save();

    // 清空畫布
    this.ctx.fillStyle = this.colors.background;
    this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // 套用相機偏移
    this.ctx.translate(offsetX, offsetY);

    // 繪製迷宮
    for (let y = 0; y < mazeGrid.length; y++) {
      for (let x = 0; x < mazeGrid[y].length; x++) {
        const cell = mazeGrid[y][x];

        if (cell === CellType.WALL) {
          this.drawWall(x, y, mazeGrid);
        } else {
          this.drawPath(x, y);
        }
      }
    }

    // 繪製答案方塊（挖洞顯示）
    blocks.forEach(block => {
      this.drawAnswerHole(block);
    });

    // 繪製怪物
    monsters.forEach(monster => {
      this.drawMonster(monster);
    });

    // 繪製玩家
    this.drawPlayer(playerPos);

    this.ctx.restore();
  }

  drawWall(x, y, mazeGrid) {
    const px = x * this.cellSize;
    const py = y * this.cellSize;
    const size = this.cellSize;
    const borderWidth = Math.max(2, Math.floor(size / 8)); // 邊框寬度

    // 繪製牆壁底色
    this.ctx.fillStyle = this.colors.wall;
    this.ctx.fillRect(px, py, size, size);

    // 檢查四個方向是否與路徑相鄰，只在相鄰處繪製橙色邊框
    const isPath = (nx, ny) => {
      if (nx < 0 || ny < 0 || ny >= mazeGrid.length || nx >= mazeGrid[0].length) {
        return false;
      }
      return mazeGrid[ny][nx] !== CellType.WALL;
    };

    this.ctx.fillStyle = this.colors.wallBorder;

    // 上邊（如果上方是路徑）
    if (isPath(x, y - 1)) {
      this.ctx.fillRect(px, py, size, borderWidth);
    }
    // 下邊（如果下方是路徑）
    if (isPath(x, y + 1)) {
      this.ctx.fillRect(px, py + size - borderWidth, size, borderWidth);
    }
    // 左邊（如果左方是路徑）
    if (isPath(x - 1, y)) {
      this.ctx.fillRect(px, py, borderWidth, size);
    }
    // 右邊（如果右方是路徑）
    if (isPath(x + 1, y)) {
      this.ctx.fillRect(px + size - borderWidth, py, borderWidth, size);
    }

    // 繪製角落（讓邊框連接更自然）
    // 左上角
    if (isPath(x - 1, y) && isPath(x, y - 1)) {
      this.ctx.fillRect(px, py, borderWidth, borderWidth);
    }
    // 右上角
    if (isPath(x + 1, y) && isPath(x, y - 1)) {
      this.ctx.fillRect(px + size - borderWidth, py, borderWidth, borderWidth);
    }
    // 左下角
    if (isPath(x - 1, y) && isPath(x, y + 1)) {
      this.ctx.fillRect(px, py + size - borderWidth, borderWidth, borderWidth);
    }
    // 右下角
    if (isPath(x + 1, y) && isPath(x, y + 1)) {
      this.ctx.fillRect(px + size - borderWidth, py + size - borderWidth, borderWidth, borderWidth);
    }
  }

  drawPath(x, y) {
    this.ctx.fillStyle = this.colors.path;
    this.ctx.fillRect(
      x * this.cellSize,
      y * this.cellSize,
      this.cellSize,
      this.cellSize
    );
  }

  // 繪製答案方塊（Wordwall 挖洞風格，支援自動換行）
  drawAnswerHole(block) {
    const px = (block.x - Math.floor(block.width / 2)) * this.cellSize;
    const py = (block.y - Math.floor(block.height / 2)) * this.cellSize;
    const width = block.width * this.cellSize;
    const height = block.height * this.cellSize;

    // 繪製深色底色
    this.ctx.fillStyle = this.colors.answerBlock;
    this.ctx.fillRect(px, py, width, height);

    // 繪製橙色邊框
    this.ctx.strokeStyle = this.colors.wallBorder;
    this.ctx.lineWidth = 4;
    this.ctx.strokeRect(px + 2, py + 2, width - 4, height - 4);

    // 繪製內部紫色光暈效果
    const gradient = this.ctx.createLinearGradient(px, py, px, py + height);
    gradient.addColorStop(0, 'rgba(179, 136, 255, 0.3)');
    gradient.addColorStop(0.5, 'rgba(179, 136, 255, 0.1)');
    gradient.addColorStop(1, 'rgba(179, 136, 255, 0.3)');
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(px + 4, py + 4, width - 8, height - 8);

    // 繪製選項文字（支援自動換行）
    const text = block.optionText;
    const maxWidth = width - 20; // 留邊距
    const maxHeight = height - 20;

    // 根據文字長度決定字體大小
    let fontSize = text.length > 30 ? 12 : text.length > 20 ? 14 : text.length > 10 ? 16 : 18;
    fontSize = Math.min(fontSize, maxHeight / 4); // 確保不超過高度

    this.ctx.font = `bold ${fontSize}px 'PingFang SC', 'Microsoft YaHei', Arial, sans-serif`;
    this.ctx.fillStyle = this.colors.answerText;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    // 文字自動換行處理
    const lines = this.wrapText(text, maxWidth, fontSize);
    const lineHeight = fontSize * 1.3;
    const totalHeight = lines.length * lineHeight;
    const startY = py + (height - totalHeight) / 2 + lineHeight / 2;

    // 繪製文字陰影
    this.ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
    this.ctx.shadowBlur = 3;
    this.ctx.shadowOffsetX = 1;
    this.ctx.shadowOffsetY = 1;

    // 繪製每一行
    lines.forEach((line, index) => {
      const y = startY + index * lineHeight;
      this.ctx.fillText(line, px + width / 2, y);
    });

    // 重置陰影
    this.ctx.shadowColor = 'transparent';
    this.ctx.shadowBlur = 0;
    this.ctx.shadowOffsetX = 0;
    this.ctx.shadowOffsetY = 0;
  }

  // 文字自動換行處理
  wrapText(text, maxWidth, fontSize) {
    this.ctx.font = `bold ${fontSize}px 'PingFang SC', 'Microsoft YaHei', Arial, sans-serif`;

    const words = text.split('');
    const lines = [];
    let currentLine = '';

    for (let i = 0; i < words.length; i++) {
      const testLine = currentLine + words[i];
      const metrics = this.ctx.measureText(testLine);

      if (metrics.width > maxWidth && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = words[i];
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    return lines;
  }

  drawPlayer(playerPos) {
    const x = playerPos.x * this.cellSize + this.cellSize / 2;
    const y = playerPos.y * this.cellSize + this.cellSize / 2;
    const size = this.cellSize;

    // 繪製玩家光暈（橙色光暈，符合狐狸主題）
    const gradient = this.ctx.createRadialGradient(x, y, 0, x, y, size * 0.9);
    gradient.addColorStop(0, 'rgba(255, 149, 0, 0.8)'); // 橙色
    gradient.addColorStop(1, 'rgba(255, 149, 0, 0)');
    this.ctx.fillStyle = gradient;
    this.ctx.beginPath();
    this.ctx.arc(x, y, size * 0.9, 0, Math.PI * 2);
    this.ctx.fill();

    // 繪製 SVG 圖片（狐狸）
    if (playerPos.sprite && playerPos.sprite.complete) {
      const imgSize = size * 1.5; // 圖片稍微大一點
      this.ctx.drawImage(
        playerPos.sprite,
        x - imgSize / 2,
        y - imgSize / 2,
        imgSize,
        imgSize
      );
    }
  }

  drawMonster(monster) {
    const x = monster.x * this.cellSize + this.cellSize / 2;
    const y = monster.y * this.cellSize + this.cellSize / 2;
    const size = this.cellSize;

    // 繪製怪物光暈（綠色，符合綠色怪物主題）
    const gradient = this.ctx.createRadialGradient(x, y, 0, x, y, size * 0.9);
    gradient.addColorStop(0, 'rgba(108, 192, 74, 0.8)'); // 綠色 (#6CC04A)
    gradient.addColorStop(1, 'rgba(108, 192, 74, 0)');
    this.ctx.fillStyle = gradient;
    this.ctx.beginPath();
    this.ctx.arc(x, y, size * 0.9, 0, Math.PI * 2);
    this.ctx.fill();

    // 繪製 SVG 圖片（怪物）
    if (monster.sprite && monster.sprite.complete) {
      const imgSize = size * 1.5; // 圖片稍微大一點
      this.ctx.drawImage(
        monster.sprite,
        x - imgSize / 2,
        y - imgSize / 2,
        imgSize,
        imgSize
      );
    }
  }

  highlightBlock(block, isCorrect) {
    const px = (block.x - Math.floor(block.width / 2)) * this.cellSize;
    const py = (block.y - Math.floor(block.height / 2)) * this.cellSize;
    const width = block.width * this.cellSize;
    const height = block.height * this.cellSize;

    // 繪製高亮顏色
    this.ctx.fillStyle = isCorrect
      ? 'rgba(39, 174, 96, 0.7)'
      : 'rgba(231, 76, 60, 0.7)';
    this.ctx.fillRect(px, py, width, height);

    // 重新繪製文字
    const text = block.optionText;
    this.ctx.fillStyle = this.colors.answerText;
    const fontSize = Math.min(width / (text.length * 0.6), height * 0.4);
    this.ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(text, px + width / 2, py + height / 2);
  }
}

// ==================== 遊戲邏輯 ====================
let renderer = null;

// 載入遊戲配置
function loadGameConfig() {
  const savedConfig = sessionStorage.getItem('MazeGameConfig');

  // 如果沒有配置，使用 demo 資料（與設定面板預設值相同）
  if (!savedConfig) {
    console.log('使用 demo 資料');
    gameConfig.value = {
      topic: 'Maze(Demo)',
      questionList: [
        {
          text: 'What is the capital of France?',
          options: [
            { text: 'Paris', isCorrect: true },
            { text: 'London', isCorrect: false },
            { text: 'Berlin', isCorrect: false },
            { text: 'Madrid', isCorrect: false }
          ]
        },
        {
          text: 'Which planet is known as the Red Planet?',
          options: [
            { text: 'Mars', isCorrect: true },
            { text: 'Venus', isCorrect: false },
            { text: 'Jupiter', isCorrect: false }
          ]
        }
      ]
    };
    return true;
  }

  try {
    gameConfig.value = JSON.parse(savedConfig);
    return true;
  } catch (error) {
    console.error('解析配置錯誤:', error);
    return false;
  }
}

// 生成新迷宮
async function generateNewMaze() {
  if (!currentQuestion.value) return;

  // 停止舊的怪物移動
  stopMonsterMovement();

  // 停止舊的玩家慣性移動
  stopPlayerMovement();

  // 清空已回答記錄（新題目重置）
  answeredBlocks.value.clear();

  // 清空已碰撞怪物記錄（新題目重置）
  collidedMonsters.value.clear();

  // 固定迷宮大小（較小更簡潔）
  const width = 40;
  const height = 24;

  // 根據當前題目的選項數量生成對應的房間
  const optionCount = currentQuestion.value.options?.length || 4;
  console.log(`📊 當前題目有 ${optionCount} 個選項`);

  // 使用預設迷宮生成器（5 種複雜迷宮隨機輪流）
  const generator = new PresetMazeGenerator(width, height, optionCount);
  maze.value = generator.generate();
  answerBlocks.value = generator.placeAnswerBlocks(currentQuestion.value.options);
  monsters.value = generator.generateMonsters(Math.min(3, optionCount - 1)); // 怪物數量不超過選項數-1

  // 設置玩家在中心位置
  const centerPos = generator.getCenterPosition();
  player.x = centerPos.x;
  player.y = centerPos.y;

  // 渲染迷宮
  if (renderer && mazeCanvas.value) {
    renderer.setupCanvas(width, height);
    renderer.render(maze.value, player, answerBlocks.value, monsters.value);
    await nextTick();
    try {
      const csw = parseInt(getComputedStyle(mazeCanvas.value).width, 10);
      const csh = parseInt(getComputedStyle(mazeCanvas.value).height, 10);
      if (Number.isFinite(csw) && csw > 0) baseCanvasW.value = csw;
      if (Number.isFinite(csh) && csh > 0) baseCanvasH.value = csh;
    } catch (_) { /* noop */ }
    updateScale();
  }

  // 更新按鈕位置
  updateControlsPosition();

  // 啟動怪物移動
  startMonsterMovement();
}

// 測試用函數已移除（改為正常的遊戲流程）

// 單步玩家移動，回傳是否成功移動一格
function movePlayer(direction) {
  if (isPaused.value || isGameOver.value) return false;

  const nextX = player.x + direction.dx;
  const nextY = player.y + direction.dy;

  // 檢查是否在迷宮範圍內
  if (!maze.value || !maze.value[nextY] || typeof maze.value[nextY][nextX] === 'undefined') {
    return false;
  }

  const nextCell = maze.value[nextY][nextX];

  // 牆壁或不可走區域就停止
  if (nextCell === CellType.WALL) {
    return false;
  }

  // 移動玩家
  player.x = nextX;
  player.y = nextY;

  // 更新按鈕位置
  updateControlsPosition();

  // 檢查是否碰到怪物
  checkMonsterCollision();

  // 檢查是否進入答案區域
  checkAnswerBlockReached();

  // 重新渲染
  renderer.render(maze.value, player, answerBlocks.value, monsters.value);

  return true;
}

// 停止玩家慣性移動
function stopPlayerMovement() {
  if (playerMoveInterval.value) {
    clearInterval(playerMoveInterval.value);
    playerMoveInterval.value = null;
  }
  currentMoveDir.value = null;
}

// 啟動玩家慣性移動：按一次就持續往該方向走，直到撞牆或邊界
function startPlayerMovement(direction) {
  if (!direction) return;
  if (isPaused.value || isGameOver.value) return;

  currentMoveDir.value = direction;

  // 每次啟動前先清掉舊的計時器
  if (playerMoveInterval.value) {
    clearInterval(playerMoveInterval.value);
    playerMoveInterval.value = null;
  }

  // 先走一步，讓按鍵有立即回饋
  const moved = movePlayer(direction);
  if (!moved) {
    // 一開始就被牆擋住，直接停止
    currentMoveDir.value = null;
    return;
  }

  // 之後持續依照同一方向移動，直到下一步被牆擋住
  const intervalMs = 250; // 每 400ms 走一格，速度稍微放慢
  playerMoveInterval.value = setInterval(() => {
    if (isPaused.value || isGameOver.value || !currentMoveDir.value) {
      stopPlayerMovement();
      return;
    }

    const ok = movePlayer(currentMoveDir.value);
    if (!ok) {
      // 撞到牆或邊界，停止慣性
      stopPlayerMovement();
    }
  }, intervalMs);
}

// 檢查怪物碰撞
function checkMonsterCollision() {
  const collidedMonster = monsters.value.find(m => m.x === player.x && m.y === player.y);
  if (collidedMonster) {
    // 使用怪物 ID 檢查是否已經碰撞過（防止重複扣分）
    const monsterId = collidedMonster.id;

    if (collidedMonsters.value.has(monsterId)) {
      // 已經碰撞過這個怪物，不重複處理
      return;
    }

    // 標記為已碰撞
    collidedMonsters.value.add(monsterId);

    // 碰到怪物 -5 分
    score.value -= 5;
    console.log('💥 碰到怪物！扣 5 分，當前分數:', score.value);

    // 顯示飛行分數效果
    showFlyingScore(-5, collidedMonster.x, collidedMonster.y);
    // 播放扣分音效（-5）
    playSound(wrongSound.value);
  }
}

// 怪物移動邏輯
function moveMonsters() {
  if (isPaused.value || isGameOver.value || !maze.value) return;

  monsters.value.forEach(monster => {
    // 隨機選擇一個方向
    const directions = [
      { dx: 0, dy: -1 }, // 上
      { dx: 1, dy: 0 },  // 右
      { dx: 0, dy: 1 },  // 下
      { dx: -1, dy: 0 }  // 左
    ];

    // 過濾出可行走的方向
    const validDirections = directions.filter(dir => {
      const nextX = monster.x + dir.dx;
      const nextY = monster.y + dir.dy;

      // 檢查邊界
      if (nextX < 0 || nextX >= maze.value[0].length || nextY < 0 || nextY >= maze.value.length) {
        return false;
      }

      // 檢查是否為可行走路徑
      return maze.value[nextY][nextX] !== CellType.WALL;
    });

    // 如果有可行走的方向，隨機選一個
    if (validDirections.length > 0) {
      const randomDir = validDirections[Math.floor(Math.random() * validDirections.length)];
      monster.x += randomDir.dx;
      monster.y += randomDir.dy;
    }
  });

  // 檢查是否碰撞
  checkMonsterCollision();

  // 重新渲染
  if (renderer) {
    renderer.render(maze.value, player, answerBlocks.value, monsters.value);
  }
}

// 啟動怪物移動
function startMonsterMovement() {
  // 清除舊的計時器
  if (monsterMoveInterval.value) {
    clearInterval(monsterMoveInterval.value);
  }

  // 每 800ms 移動一次怪物
  monsterMoveInterval.value = setInterval(moveMonsters, 800);
}

// 停止怪物移動
function stopMonsterMovement() {
  if (monsterMoveInterval.value) {
    clearInterval(monsterMoveInterval.value);
    monsterMoveInterval.value = null;
  }
}

// 檢查是否進入答案區域
function checkAnswerBlockReached() {
  const block = answerBlocks.value.find(b => {
    const halfWidth = Math.floor(b.width / 2);
    const halfHeight = Math.floor(b.height / 2);
    return player.x >= b.x - halfWidth &&
           player.x <= b.x + halfWidth &&
           player.y >= b.y - halfHeight &&
           player.y <= b.y + halfHeight;
  });

  if (block) {
    // 使用唯一標識符檢查是否已經回答過（防止重複扣分）
    const blockId = `${block.x}_${block.y}_${block.optionIndex}`;

    if (answeredBlocks.value.has(blockId)) {
      // 已經回答過這個方塊，不重複處理
      return;
    }

    // 標記為已回答
    answeredBlocks.value.add(blockId);

    if (block.isCorrect) {
      handleCorrectAnswer(block);
    } else {
      handleWrongAnswer(block);
    }
  }
}

// 答對處理
function handleCorrectAnswer(block) {
  score.value += 10;
  feedbackMessage.value = t('Mazegame.correctAnswerMsg');
  feedbackType.value = 'success';

  // 高亮正確方塊
  renderer.highlightBlock(block, true);

  // 顯示飛行分數效果
  showFlyingScore(10, block.x, block.y);

  // 播放答對音效
  playSound(correctSound.value);

  console.log('✅ 答對了！分數:', score.value);

  setTimeout(() => {
    feedbackMessage.value = '';

    // Demo 模式：答對後直接進入下一題（不跳題）
    if (currentQuestionIndex.value < gameConfig.value.questionList.length - 1) {
      currentQuestionIndex.value++;
      generateNewMaze();
    } else {
      // 所有題目完成，結束遊戲
      endGame();
    }
  }, 1500);
}

// 答錯處理
function handleWrongAnswer(block) {
  wrongAttempts.value++;
  score.value -= 1; // 答錯 -1 分（可以為負數）
  feedbackMessage.value = t('Mazegame.wrongAnswer');
  feedbackType.value = 'error';

  // 高亮錯誤方塊
  renderer.highlightBlock(block, false);

  // 顯示飛行分數效果
  showFlyingScore(-1, block.x, block.y);

  // 播放答錯音效
  playSound(wrongSound.value);

  console.log('❌ 答錯了！錯誤次數:', wrongAttempts.value, '分數:', score.value);

  setTimeout(() => {
    feedbackMessage.value = '';
    // Demo 模式：答錯後繼續當前題目（不跳題）
    renderer.render(maze.value, player, answerBlocks.value, monsters.value);
  }, 1500);
}

// 🆕 處理 HiTeach 名單選擇
const handleRosterSelection = (event) => {
  const selectedValue = event.target.value;

  if (!selectedValue) {
    studentInfo.name = '';
    studentInfo.seatNumber = '';
    return;
  }

  const selectedMember = classRoster.value.find(
    member => `${member.memberName}-${member.seatID}` === selectedValue
  );

  if (selectedMember) {
    studentInfo.name = selectedMember.memberName;
    studentInfo.seatNumber = String(selectedMember.seatID);
    console.log('👤 選擇學生 (Maze):', selectedMember);
  }
};

// ==================== 遊戲控制函數 ====================
// Ready, Go! 倒計時
const showReadyGoCountdown = () => {
  // 正式活動模式：驗證學生資訊
  if (isFromActId.value) {
    // 清空錯誤訊息
    studentInfoErrors.name = '';
    studentInfoErrors.seatNumber = '';

    // 驗證姓名
    if (!studentInfo.name.trim()) {
      studentInfoErrors.name = t('gameActManager.studentNameRequired');
      return;
    }
    if (studentInfo.name.length > 20) {
      studentInfoErrors.name = t('gameActManager.studentNameTooLong');
      return;
    }

    // 驗證座號（選填，但如果填寫必須符合格式）
    if (studentInfo.seatNumber && !/^\d{1,3}$/.test(studentInfo.seatNumber)) {
      studentInfoErrors.seatNumber = t('gameActManager.seatNumberInvalid');
      return;
    }

    // 儲存到 sessionStorage
    sessionStorage.setItem('studentInfo', JSON.stringify({
      name: studentInfo.name.trim(),
      seatNumber: studentInfo.seatNumber.trim()
    }));
  }

  beforeGame.value = false;
  showReadyGo.value = true;
  readyGoText.value = 'Ready';
  playSound(readySound.value);

  setTimeout(() => {
    readyGoText.value = 'Go!';
    playSound(goSound.value);
    try {
      if (correctSound.value) {
        const v = correctSound.value.volume;
        correctSound.value.volume = Math.min(0.01, v);
        const p = correctSound.value.play();
        if (p && typeof p.then === 'function') {
          p.then(() => { correctSound.value.pause(); correctSound.value.currentTime = 0; correctSound.value.volume = v; }).catch(() => { correctSound.value.volume = v; });
        } else {
          correctSound.value.pause(); correctSound.value.currentTime = 0; correctSound.value.volume = v;
        }
      }
      if (wrongSound.value) {
        const v2 = wrongSound.value.volume;
        wrongSound.value.volume = Math.min(0.01, v2);
        const p2 = wrongSound.value.play();
        if (p2 && typeof p2.then === 'function') {
          p2.then(() => { wrongSound.value.pause(); wrongSound.value.currentTime = 0; wrongSound.value.volume = v2; }).catch(() => { wrongSound.value.volume = v2; });
        } else {
          wrongSound.value.pause(); wrongSound.value.currentTime = 0; wrongSound.value.volume = v2;
        }
      }
    } catch (e) { void 0; }
    try {
      if (backgroundMusic.value) {
        backgroundMusic.value.currentTime = 0;
        backgroundMusic.value.play();
      }
    } catch (e) {
      // ignore
    }
    setTimeout(() => {
      showReadyGo.value = false;
      startGame();
    }, 500);
  }, 800);
};

// 開始遊戲
const startGame = () => {
  gameStarted.value = true;
  isPaused.value = false;
  startTime.value = Date.now();
  loadAnimalImages(); // 每次開始遊戲隨機選擇動物
  generateNewMaze();

  // 首次開始遊戲時播放 Canvas 放大動畫（zoom 1 -> 1.25）
  if (!hasPlayedZoomAnimation.value) {
    hasPlayedZoomAnimation.value = true;
    startCanvasZoomAnimation(1, 1.25, 600);
  }
  try {
    if (backgroundMusic.value) {
      backgroundMusic.value.currentTime = 0;
      backgroundMusic.value.play();
    }
  } catch (e) {
    // ignore
  }
};

// 暫停/繼續
function togglePause() {
  if (!gameStarted.value) {
    showReadyGoCountdown();
  } else if (isPaused.value) {
    resumeGame();
  } else {
    pauseGame();
  }
}

// 前往設定頁面
const goToSettings = () => {
  // 先將當前遊戲配置儲存到 sessionStorage，讓設定頁面可以讀取
  const config = {
    topic: gameConfig.value?.topic || 'Maze(Demo)',
    questionList: gameConfig.value?.questionList || []
  };
  if (pushedActivityId.value) {
    config.useHiTeachRoster = useHiTeachRoster.value;
    config.restoredData = { activityId: pushedActivityId.value, shortUrl: pushedShortUrl.value };
  }
  sessionStorage.setItem('MazeGameConfig', JSON.stringify(config));

  router.push('/ht-mazegame-setting');
};

// 已移至 useGameActivityPush composable：
// - createGameActivityFromCurrentConfig
// - generateShortUrl
// - updateActivityWithShortUrl
// - createAndPushShortUrl

// 推送到終端（舊版本 HiTeach）
const sendToWebIRS = () => {
  messageBridge.postMessage({
    action: 'SendGame.WebIRS',
    data: {
      game: 'mazeGame',
      gameConfig: JSON.stringify({
        topic: gameConfig.value?.topic || 'Maze(Demo)',
        questionList: gameConfig.value?.questionList || []
      })
    }
  });
};

// 實際執行推送邏輯
const executePush = async () => {
  console.log('HiTeach 版本：', htVersion.value, '支援新 Push 功能：', isNewPushFuncVersion.value);

  if (isPushing.value) return;
  isPushing.value = true;

  try {
    if (isNewPushFuncVersion.value) {
      // 新版本 HiTeach (>= 5.5.0.0002)：創建活動 + 推送短網址
      console.log('[HTMazeGamePlay] 使用新版 Push 功能（創建活動 + 短網址）');

      // ✨ 修改：接收返回值
      const result = await createAndPushShortUrl(gameConfig.value, 1, { skipPush: !htStartClass.value }); // Maze 遊戲 playerCount 固定為 1

      // ✨ 新增：更新最新推送的活動 ID（覆蓋舊值）
      if (result.success && result.activityId) {
        pushedActivityId.value = result.activityId;
        pushedActivityName.value = gameConfig.value?.topic || '迷宮遊戲';
        pushedShortUrl.value = result.shortUrl || '';
        // ✅ 同步更新 classRosterSource（根據 useHiTeachRoster 判斷）
        classRosterSource.value = gameConfig.value?.useHiTeachRoster ? 'HT' : 'general';
        // 通知 HT 儲存當前遊戲設定與 restoredData（供翻頁恢復使用）
        messageBridge.postMessage({
            action: 'Save.MazeGameSetting',
            data: {
                ...gameConfig.value,
                restoredData: {
                    activityId: result.activityId,
                    shortUrl: result.shortUrl
                }
            }
        });
        console.log('[HTMazeGamePlay] 已更新最新推送活動 ID:', result.activityId);
        console.log('[HTMazeGamePlay] 已更新 classRosterSource:', classRosterSource.value);
        console.log('[HTMazeGamePlay] ⚠️ 注意：這會覆蓋之前的 activityId');
      }
    } else {
      // 舊版本 HiTeach 或無版本資訊：推送完整 JSON（維持現有邏輯）
      console.log('[HTMazeGamePlay] 使用舊版 Push 功能（推送 JSON）');
      sendToWebIRS();
      $Message.success({
        content: t('Mazegame.sendSuccess'),
        duration: 3,
        closable: true
      });
      // 注意：舊版本無法取得 activityId，查看成績按鈕不會顯示
    }
  } catch (error) {
    console.error('[HTMazeGamePlay] Push 失敗:', error);
    // 錯誤訊息已在 createAndPushShortUrl 中顯示
  }

  isPushing.value = false;
};

// 重新推送目前的短網址（不重新上傳）
const repush = async () => {
  if (!pushedShortUrl.value || !pushedActivityId.value) return;
  // 先詢問 HT 目前是否開始課堂
  const inClass = await queryHTStartClass();
  if (!inClass) {
    $Message.warning({
      content: t('gameActManager.noClassCannotPush'),
      duration: 3,
      closable: true
    });
    return;
  }
  const originalUrl = `${window.location.origin}/ht-mazegame-play?actId=${pushedActivityId.value}`;
  messageBridge.postMessage({
    action: 'SendGameLink.WebIRS',
    data: {
      url: originalUrl,
      shortUrl: pushedShortUrl.value,
      activityId: pushedActivityId.value,
      gameType: 'mazegame'
    }
  });
  $Message.success({
    content: t('Game.pushSuccess') || '重新推送成功',
    duration: 2,
    closable: true
  });
};

// 處理發送協議按鈕點擊（WebView 環境）
const handleSendClick = async () => {
  // 先詢問 HT 目前是否開始課堂
  htStartClass.value = await queryHTStartClass();
  showPushConfirm.value = true;
};

// ✨ 開啟成績查看面板
const openScoresPanel = () => {
  if (!pushedActivityId.value) {
    $Message.warning({
      content: t('HTwbActKitHome.noActivityPushed') || '尚未推送活動，無法查看成績',
      duration: 3,
      closable: true
    });
    return;
  }
  showScoresPanel.value = true;
};

// ✨ 關閉成績查看面板
const closeScoresPanel = () => {
  showScoresPanel.value = false;
};

// 複製短網址
const copyPushedShortUrl = () => {
    if (!pushedShortUrl.value) return;
    navigator.clipboard.writeText(pushedShortUrl.value).then(() => {
        $Message.success({ content: t('gameActManager.shortUrlCopied') || '已複製連結', duration: 2 });
    }).catch(() => {
        $Message.error({ content: t('gameActManager.copyFailed') || '複製失敗', duration: 2 });
    });
};

// 顯示 QR Code
const showPushedQRCode = async () => {
    showQRCodeOverlay.value = true;
    await nextTick();
    if (!qrcodeCanvas.value || !pushedShortUrl.value) return;
    try {
        await QRCode.toCanvas(qrcodeCanvas.value, pushedShortUrl.value, {
            width: 280,
            margin: 2,
            color: { dark: '#000000', light: '#FFFFFF' }
        });
    } catch (error) {
        console.error('生成 QR Code 失敗:', error);
    }
};

const closeQRCodeOverlay = () => {
    showQRCodeOverlay.value = false;
};

// 開啟遊戲雲端管理頁面
const openCloudManagement = () => {
    router.push('/game-act-manager');
};

// 暫停遊戲
const pauseGame = () => {
  isPaused.value = true;
  stopMonsterMovement();
  stopPlayerMovement();
  try { backgroundMusic.value && backgroundMusic.value.pause(); } catch (e) { void 0; }
};

// 視窗大小調整處理
const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

// 關閉小螢幕警告
const dismissWarning = () => {
  isWarningDismissed.value = true;
};

// 繼續遊戲
const resumeGame = () => {
  isPaused.value = false;
  startMonsterMovement();
  try { backgroundMusic.value && backgroundMusic.value.play(); } catch (e) { void 0; }
};

// 重新開始
function restartGame() {
  currentQuestionIndex.value = 0;
  score.value = 0;
  wrongAttempts.value = 0;
  isGameOver.value = false;
  beforeGame.value = true;
  gameStarted.value = false;
  isPaused.value = false;
  scoreSubmitted.value = false;
  submitResult.value = null;
  showReadyGo.value = false;
  answeredBlocks.value.clear(); // 清空已回答記錄
  stopMonsterMovement();
   stopPlayerMovement();
  try {
    if (backgroundMusic.value) {
      backgroundMusic.value.pause();
      backgroundMusic.value.currentTime = 0;
    }
  } catch (e) { void 0; }
}

// 提交遊戲成績到後端
const submitGameScore = async () => {
  // 防抖檢查：如果已經提交過，直接返回
  if (scoreSubmitted.value) {
    console.log('成績已經提交過，跳過重複提交');
    return;
  }

  // 只在正式活動模式且沒有切換遊戲時提交成績
  if (!isFromActId.value) {
    console.log('非正式活動模式，不提交成績');
    return;
  }

  if (hasGameSwitched.value) {
    console.log('因切換遊戲模板，不提交成績');
    return;
  }

  // 設置旗標，防止重複提交
  scoreSubmitted.value = true;

  try {
    // 從 sessionStorage 取得學生資訊
    const studentData = JSON.parse(sessionStorage.getItem('studentInfo') || '{}');

    // 從 URL 取得 actId
    const urlParams = new URLSearchParams(window.location.search);
    const actId = urlParams.get('actId');

    if (!actId) {
      console.error('無法提交成績：缺少 actId');
      scoreSubmitted.value = false;
      return;
    }

    if (!studentData.name) {
      console.error('無法提交成績：缺少學生姓名');
      scoreSubmitted.value = false;
      return;
    }

    // 計算正確題數（完成的題目數）
    const correctCount = currentQuestionIndex.value;

    const scoreData = {
      activityId: actId,
      studentName: studentData.name,
      seatNumber: studentData.seatNumber || '',
      score: score.value,
      correctCount: correctCount,
      wrongCount: wrongAttempts.value
    };

    console.log('提交成績資料（迷宮遊戲）:', scoreData);
    const response = await gameActivityAPI.submitScore(scoreData);

    if (response.status === 200) {
      console.log('成績提交成功:', response.data);
      submitResult.value = response.data;
    }
  } catch (error) {
    console.error('提交成績失敗:', error);
    scoreSubmitted.value = false; // 失敗時重置旗標，允許重試
  }
};

// 結束遊戲
const endGame = () => {
  // 防抖檢查：如果遊戲已經結束，直接返回
  if (isGameOver.value) {
    console.log('遊戲已經結束，避免重複調用 endGame');
    return;
  }

  isGameOver.value = true;
  gameStarted.value = false;
  stopMonsterMovement();
  stopPlayerMovement();

  // 計算遊戲時長
  if (startTime.value) {
    timeSpent.value = Math.floor((Date.now() - startTime.value) / 1000);
  }

  // 播放遊戲結束音效
  playSound(gameOverSound.value);
  try {
    if (backgroundMusic.value) {
      backgroundMusic.value.pause();
      backgroundMusic.value.currentTime = 0;
    }
  } catch (e) { void 0; }

  // 提交成績（正式活動模式）
  if (isFromActId.value && !hasGameSwitched.value) {
    submitGameScore();
  }
};

// 格式化時間
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// 切換答案彈窗
function toggleAnswerModal() {
  showAnswerModal.value = !showAnswerModal.value;
}

// 顯示飛行分數效果（完全複製貪食蛇）
function showFlyingScore(points, gridX, gridY) {
  if (!renderer || !mazeCanvas.value) return;

  const cellSize = renderer.cellSize || 20;

  // 計算畫布相對於視窗的位置
  const canvasRect = mazeCanvas.value.getBoundingClientRect();

  // 計算飛行分數的位置（轉換為視窗座標）
  const scaledCell = cellSize * (scale.value || 1);
  const offsetX = (renderer.viewOffsetX || 0) * (scale.value || 1);
  const offsetY = (renderer.viewOffsetY || 0) * (scale.value || 1);
  const x = canvasRect.left + gridX * scaledCell + scaledCell / 2 + offsetX;
  const y = canvasRect.top + gridY * scaledCell + scaledCell / 2 + offsetY;

  const id = Date.now() + Math.random();
  const color = points > 0 ? '#4caf50' : '#f44336'; // 綠色為正分，紅色為負分
  const text = points > 0 ? `+${points}` : `${points}`;

  flyingScores.value.push({ id, x, y, color, text });

  // 1 秒後移除
  setTimeout(() => {
    const index = flyingScores.value.findIndex(fs => fs.id === id);
    if (index !== -1) {
      flyingScores.value.splice(index, 1);
    }
  }, 1000);
}

// Canvas 初始縮放動畫：將 renderer.zoom 從 fromZoom 漸變到 toZoom
// 會先在 fromZoom 狀態停留一小段時間，再開始放大
function startCanvasZoomAnimation(fromZoom = 1, toZoom = 1.25, duration = 600) {
  if (!renderer || zoomAnimating.value) return;

  zoomAnimating.value = true;
  const holdDuration = 1000; // 在原始縮放狀態停留的時間(ms)
  const startTimeStamp = performance.now();

  const animate = (now) => {
    const elapsed = now - startTimeStamp;

    // 先在 fromZoom 停留 holdDuration，再進入縮放區間
    if (elapsed < holdDuration) {
      renderer.zoom = fromZoom;
      if (maze.value && renderer && player) {
        renderer.render(maze.value, player, answerBlocks.value, monsters.value);
      }
      requestAnimationFrame(animate);
      return;
    }

    const zoomElapsed = elapsed - holdDuration;
    const t = Math.min(1, zoomElapsed / duration);

    // 簡單 ease-out
    const easedT = 1 - Math.pow(1 - t, 3);
    const currentZoom = fromZoom + (toZoom - fromZoom) * easedT;
    renderer.zoom = currentZoom;

    // 重新渲染當前畫面
    if (maze.value && renderer && player) {
      renderer.render(maze.value, player, answerBlocks.value, monsters.value);
    }

    if (t < 1) {
      requestAnimationFrame(animate);
    } else {
      renderer.zoom = toZoom;
      zoomAnimating.value = false;
    }
  };

  // 初始化 zoom 值並開始動畫
  renderer.zoom = fromZoom;
  requestAnimationFrame(animate);
}

// 鍵盤控制
function handleKeyPress(e) {
  const keyMap = {
    ArrowUp: { dx: 0, dy: -1 },
    ArrowDown: { dx: 0, dy: 1 },
    ArrowLeft: { dx: -1, dy: 0 },
    ArrowRight: { dx: 1, dy: 0 },
    KeyW: { dx: 0, dy: -1 },
    KeyS: { dx: 0, dy: 1 },
    KeyA: { dx: -1, dy: 0 },
    KeyD: { dx: 1, dy: 0 },
    Escape: null
  };

  if (e.code === 'Escape') {
    togglePause();
    e.preventDefault();
    return;
  }

  if (keyMap[e.code]) {
    e.preventDefault();
    startPlayerMovement(keyMap[e.code]);
  }
}

// 音效工具函數
const playSound = (sound) => {
  if (!sound) return;
  try {
    const isSfx = sound === correctSound.value || sound === wrongSound.value;
    let prevBgmVol = null;
    if (isSfx && backgroundMusic.value) {
      prevBgmVol = backgroundMusic.value.volume;
      backgroundMusic.value.volume = Math.min(prevBgmVol, 0.1);
    }
    sound.pause();
    sound.currentTime = 0;
    const p = sound.play();
    if (isSfx && backgroundMusic.value) {
      setTimeout(() => {
        try { if (prevBgmVol !== null) backgroundMusic.value.volume = prevBgmVol; } catch (e) { void 0; }
      }, 600);
    }
    if (p && typeof p.catch === 'function') { p.catch(() => {}); }
  } catch (e) {
    console.error('播放音效失敗:', e);
  }
};

// 初始化音效
const initSounds = () => {
  readySound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-ready.mp3'));
  goSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-go.mp3'));
  gameOverSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/orchestral-win-331233.mp3'));
  correctSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/short-success-sound-glockenspiel-treasure-video-game-6346.mp3'));
  wrongSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/wrong-answer-129254.mp3'));
  backgroundMusic.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/quiz-evaluation-loop-thinking-time-231582.mp3'));
  backgroundMusic.value.loop = true;
  backgroundMusic.value.volume = 0.2;
  correctSound.value.volume = 1.0;
  wrongSound.value.volume = 1.0;

  readySound.value.load();
  goSound.value.load();
  gameOverSound.value.load();
  correctSound.value && correctSound.value.load();
  wrongSound.value && wrongSound.value.load();
  backgroundMusic.value && backgroundMusic.value.load();
};

// ==================== 預覽模式相關 ====================
// 載入預覽模式參數
const loadPreviewMode = (params) => {
  console.log('🧩 [預覽模式] 收到的完整參數:', params);
  isPreviewMode.value = true;
  console.log('🏷️ [預覽模式] isPreviewMode 已設定為:', isPreviewMode.value);

  // 從 params.gameParameters 提取遊戲參數
  const gp = params.gameParameters || {};
  console.log('📋 [預覽模式] gameParameters:', gp);
  console.log('📝 [預覽模式] questionList 數量:', gp.questionList?.length || 0);

  // 映射到 gameConfig 格式（單選題迷宮使用 questionList）
  gameConfig.value = {
    topic: gp.topic || '單選題迷宮預覽',
    questionList: gp.questionList || []
  };

  console.log('✅ [預覽模式] gameConfig 已更新:', {
    topic: gameConfig.value.topic,
    questionCount: gameConfig.value.questionList.length
  });

  // 顯示預覽模式提示訊息（WebView 環境不顯示，延遲顯示確保頁面已完全載入）
  if (!isWebView.value) {
    setTimeout(() => {
      try {
        if ($Message && typeof $Message.info === 'function') {
          $Message.info({
            content: t('gameActManager.previewRefreshWarning'),
            duration: 10,
            closable: true
          });
        } else {
          console.warn('[迷宮預覽模式] $Message 未正確注入，使用 console 提示');
          console.info(`📢 ${t('gameActManager.previewRefreshWarning')}`);
        }
      } catch (error) {
        console.error('[迷宮預覽模式] 顯示提示訊息失敗:', error);
        console.info(`📢 ${t('gameActManager.previewRefreshWarning')}`);
      }
    }, 500);
  }
};

// 預覽模式下的頁面重新整理警告
const handleBeforeUnload = (event) => {
  if (isPreviewMode.value) {
    // 現代瀏覽器要求設定 returnValue 為空字串才會顯示確認對話框
    event.preventDefault();
    event.returnValue = ''; // 必須設定為空字串
    return ''; // 相容舊版瀏覽器
  }
};

// 初始化 Auth Token
const initializeAuth = async () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("auth_token_expiry");
  try {
    const res = await api.main.getTokenUnlogin();
    const exp = jwt_decode(res.auth_token).exp;
    localStorage.setItem("auth_token", res.auth_token);
    localStorage.setItem("auth_token_expiry", exp);
  } catch (error) {
    console.error("Error fetching auth token", error);
  }
};

// ==================== 生命週期 ====================
onMounted(async () => {
  console.log('🧩 迷宮遊戲掛載');

  // 初始化音效
  initSounds();

  // 載入 HiTeach 版本號
  loadVersion();

  // 0. 檢查是否從其他遊戲切換過來
  const urlParams = new URLSearchParams(window.location.search);
  const switched = urlParams.get('switched');

  if (switched === '1' || sessionStorage.getItem('hasGameSwitched') === 'true') {
    hasGameSwitched.value = true;
    console.log('🔄 檢測到遊戲切換，將不會提交成績');
  }

  // 1. 最優先檢查 actId 參數（正式活動模式）
  const actId = urlParams.get('actId');

  if (actId) {
    isFromActId.value = true;
    Loading.show({ message: t('Game.gameLoading') });

    // 先初始化 auth_token（actId 模式需要匿名 token）
    await initializeAuth();

    try {
      // 呼叫公開 API 獲取活動資料
      const response = await gameActivityAPI.getGameActivity({ id: actId });
      console.log('🎮 API Response:', response);
      const activity = response.data;
      console.log('🎯 Activity Data:', activity);

      // 解析 HiTeach 名單設定
      classRosterSource.value = activity.classRosterSource || '';
      try {
        const gameParameters = activity.gameContentSettings
          ? JSON.parse(activity.gameContentSettings)
          : {};
        useHiTeachRoster.value = gameParameters.useHiTeachRoster || false;
      } catch (e) {
        useHiTeachRoster.value = false;
      }
      if (classRosterSource.value === 'HT' && activity.classRoster) {
        try {
          classRoster.value = JSON.parse(activity.classRoster);
          console.log('👥 HiTeach 名單 (Maze):', classRoster.value);
        } catch (error) {
          console.error('❌ 解析 Maze classRoster 失敗:', error);
          classRoster.value = [];
        }
      }

      // 檢查活動是否開放進入
      if (!activity.enableEntry) {
        $Message.warning({
          content: t('gameActManager.activityNotOpen'),
          duration: 0,
          closable: true
        });
        beforeGame.value = false;
        return;
      }

      // 檢查 currentGame 是否與當前頁面匹配
      if (validateAndRedirectGame(activity, ['maze'], router, actId, switched)) {
        return; // 已重導向，中斷後續邏輯
      }

      // 讀取是否允許切換遊戲模板
      allowTemplateChange.value = activity.allowTemplateChange ?? false;
      console.log('🔀 Allow Template Change:', allowTemplateChange.value);

      // 檢查是否為原始遊戲（用於判斷是否提交成績）
      const originalGame = activity.currentGame;
      const isOriginalGame = originalGame === 'maze';
      console.log('🎯 Original Game:', originalGame, 'Is Original:', isOriginalGame);

      if (!isOriginalGame && !hasGameSwitched.value) {
        hasGameSwitched.value = true;
        console.log('🔄 此活動原始遊戲不是迷宮，標記為已切換');
      }

      // 解析 gameContentSettings (JSON 字串)
      console.log('📝 Raw gameContentSettings:', activity.gameContentSettings);
      const gameParameters = activity.gameContentSettings
        ? JSON.parse(activity.gameContentSettings)
        : {};

      console.log('✅ Parsed Game Parameters:', gameParameters);

      // 檢查 gameParameters 是否為空，若為空則使用預設值
      if (Object.keys(gameParameters).length === 0) {
        console.warn('⚠️ gameContentSettings 是空的，使用預設遊戲參數');
        // 迷宮遊戲預設參數（與設定面板預設值相同）
        gameConfig.value = {
          topic: activity.activityName,
          questionList: [
            {
              text: 'What is the capital of France?',
              options: [
                { text: 'Paris', isCorrect: true },
                { text: 'London', isCorrect: false },
                { text: 'Berlin', isCorrect: false },
                { text: 'Madrid', isCorrect: false }
              ]
            },
            {
              text: 'Which planet is known as the Red Planet?',
              options: [
                { text: 'Mars', isCorrect: true },
                { text: 'Venus', isCorrect: false },
                { text: 'Jupiter', isCorrect: false }
              ]
            }
          ]
        };
      } else {
        // 映射到 gameConfig 格式
        gameConfig.value = {
          topic: activity.activityName,
          questionList: gameParameters.questionList || []
        };
      }

      console.log('🎲 Final gameConfig:', gameConfig.value);

      // 顯示活動載入成功訊息
      $Message.success({
        content: t('gameActManager.activityLoadSuccess'),
        duration: 2,
        closable: true
      });

      // 🆕 檢查 webirs_seatid 和 webirs_name 參數，自動帶入學生資訊
      const webirsSeatId = urlParams.get('webirs_seatid');
      const webirsName = urlParams.get('webirs_name');

      if (webirsSeatId || webirsName) {
        console.log('📋 檢測到 WebIRS 參數:', { webirsSeatId, webirsName });

        // 使用 nextTick 確保 DOM 已更新
        await nextTick();

        if (classRosterSource.value === 'HT' && classRoster.value.length > 0) {
          // HiTeach 名單模式：根據座號找到對應的學生並選中
          const matchedMember = classRoster.value.find(
            member => String(member.seatID) === webirsSeatId
          );

          if (matchedMember) {
            // 設定學生資訊
            studentInfo.name = matchedMember.memberName;
            studentInfo.seatNumber = String(matchedMember.seatID);

            // 設定下拉選單的值（需要等待 DOM 渲染）
            await nextTick();
            const selectElement = document.querySelector('.student-select');
            if (selectElement) {
              selectElement.value = `${matchedMember.memberName}-${matchedMember.seatID}`;
            }

            console.log('✅ [HT名單模式] 已自動選中學生:', matchedMember);
          } else if (webirsName) {
            // 如果座號沒有匹配到，但有名字參數，嘗試用名字匹配
            const matchedByName = classRoster.value.find(
              member => member.memberName === decodeURIComponent(webirsName)
            );

            if (matchedByName) {
              studentInfo.name = matchedByName.memberName;
              studentInfo.seatNumber = String(matchedByName.seatID);

              await nextTick();
              const selectElement = document.querySelector('.student-select');
              if (selectElement) {
                selectElement.value = `${matchedByName.memberName}-${matchedByName.seatID}`;
              }

              console.log('✅ [HT名單模式] 透過名字匹配到學生:', matchedByName);
            } else {
              console.warn('⚠️ [HT名單模式] 未找到匹配的學生，座號:', webirsSeatId, '名字:', webirsName);
            }
          }
        } else {
          // 手動輸入模式：直接帶入座號和名字
          if (webirsSeatId) {
            studentInfo.seatNumber = webirsSeatId;
          }
          if (webirsName) {
            studentInfo.name = decodeURIComponent(webirsName);
          }
          console.log('✅ [手動輸入模式] 已自動填入學生資訊:', {
            seatNumber: studentInfo.seatNumber,
            name: studentInfo.name
          });
        }
      }

      // 初始化渲染器
      renderer = new MazeRenderer(mazeCanvas.value);

      // 註冊鍵盤事件
      window.addEventListener('keydown', handleKeyPress);

      return; // actId 模式設置完成，結束 onMounted
    } catch (error) {
      console.error('❌ 載入活動資料失敗:', error);
      $Message.error(t('gameActManager.activityLoadFailed'));
      return;
    } finally {
      Loading.hide();
    }
  }

  // 2. 檢查預覽模式
  const previewData = localStorage.getItem('game_preview_data');
  const previewTimestamp = localStorage.getItem('game_preview_timestamp');

  console.log('[迷宮] localStorage 檢查:', {
    hasPreviewData: !!previewData,
    hasTimestamp: !!previewTimestamp,
    previewDataLength: previewData?.length || 0
  });

  if (previewData && previewTimestamp) {
    const now = Date.now();
    const timestamp = parseInt(previewTimestamp, 10);
    const fiveMinutes = 5 * 60 * 1000;
    const timeElapsed = now - timestamp;

    console.log('[迷宮] 時效檢查:', {
      timeElapsed: `${(timeElapsed / 1000).toFixed(1)}秒`,
      isValid: timeElapsed < fiveMinutes,
      threshold: `${fiveMinutes / 1000}秒`
    });

    // 檢查是否在 5 分鐘內
    if (now - timestamp < fiveMinutes) {
      try {
        const params = JSON.parse(previewData);

        console.log('[迷宮] 預覽參數解析成功:', {
          currentGame: params.currentGame,
          playerCount: params.playerCount,
          hasGameParameters: !!params.gameParameters
        });

        // 僅當 currentGame 為 'maze' 時才載入預覽模式
        if (params.currentGame === 'maze') {
          loadPreviewMode(params);

          // 初始化渲染器
          renderer = new MazeRenderer(mazeCanvas.value);

          // 延遲清除 localStorage（30秒後），避免頁面重新整理時資料遺失
          setTimeout(() => {
            localStorage.removeItem('game_preview_data');
            localStorage.removeItem('game_preview_timestamp');
            console.log('[迷宮] 預覽資料已清除（30秒後）');
          }, 30000); // 30秒後清除

          // 添加頁面重新整理警告
          window.addEventListener('beforeunload', handleBeforeUnload);

          // 註冊鍵盤事件
          window.addEventListener('keydown', handleKeyPress);

          // 跳出，不執行後續邏輯
          return;
        } else {
          console.log('[迷宮] currentGame 不是 maze，跳過預覽模式載入');
        }
      } catch (error) {
        console.error('[迷宮] 預覽參數解析失敗:', error);
        localStorage.removeItem('game_preview_data');
        localStorage.removeItem('game_preview_timestamp');
      }
    } else {
      console.log('[迷宮] 預覽資料已過期，清除 localStorage');
      localStorage.removeItem('game_preview_data');
      localStorage.removeItem('game_preview_timestamp');
    }
  }

  // 2. 如果沒有預覽模式，使用預設 Demo 模式
  console.log('[迷宮] 使用預設 Demo 模式');

  if (!loadGameConfig()) return;

  // 3. 發送初始化訊息，並接收遊戲配置（WebView/Demo 模式）
  messageBridge.postMessage({
    action: 'Init.MazeGame',
    data: JSON.stringify({
      topic: gameConfig.value?.topic || 'Maze(Demo)',
      questionList: gameConfig.value?.questionList || []
    })
  });

  // 保存監聽器引用以便在 onUnmounted 時移除
  const mazeGameMessageHandler = (event) => {
    const msg = event.data;
    console.log(msg,"Maze頁面");

    // 接收 HT 上課狀態回應
    if (msg.action === 'Send.HTStartClass') {
      const inClass = msg.data?.HTStartClass ?? true;
      htStartClass.value = inClass;
      if (htStartClassPending) {
        htStartClassPending(inClass);
        htStartClassPending = null;
      }
      return;
    }

    if (msg.action === 'Init.MazeGame') {
      // 解析 JSON 字串
      const parsedData = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;

      // 若新設定不含 restoredData，嘗試從 sessionStorage 保留（設定未變動時維持已推送狀態）
      if (!parsedData.restoredData) {
        try {
          const existingConfig = JSON.parse(sessionStorage.getItem('MazeGameConfig') || '{}');
          if (existingConfig.restoredData) {
            const existComp = JSON.stringify({ topic: existingConfig.topic, questionList: existingConfig.questionList, useHiTeachRoster: existingConfig.useHiTeachRoster });
            const newComp = JSON.stringify({ topic: parsedData.topic, questionList: parsedData.questionList, useHiTeachRoster: parsedData.useHiTeachRoster });
            if (existComp === newComp) parsedData.restoredData = existingConfig.restoredData;
          }
        } catch (e) { /* sessionStorage 讀取失敗，略過保留 */ }
      }

      // 使用 Object.assign 更新遊戲配置
      Object.assign(gameConfig.value, {
        topic: parsedData.topic || gameConfig.value.topic,
        questionList: parsedData.questionList || gameConfig.value.questionList
      });
      // 同步 useHiTeachRoster ref（校園版預設勾選）
      if (parsedData.useHiTeachRoster !== undefined) {
        useHiTeachRoster.value = !!parsedData.useHiTeachRoster;
      } else if (schoolVers.value) {
        useHiTeachRoster.value = true;
      }

      // 將設定存入 sessionStorage
      sessionStorage.setItem('MazeGameConfig', JSON.stringify(parsedData));
      // 若帶有 restoredData，恢復推送狀態（短網址、查看成績按鈕）
      if (parsedData.restoredData?.activityId) {
          pushedActivityId.value = parsedData.restoredData.activityId;
          pushedShortUrl.value = parsedData.restoredData.shortUrl || '';
          pushedActivityName.value = gameConfig.value.topic || '迷宮遊戲';
          console.log('[HTMazeGamePlay] 恢復推送狀態 activityId:', parsedData.restoredData.activityId);
      }
    }

    // 注意：Init.GameHome 已由 useGameMessageBridge composable 統一處理
  };
  messageBridge.addEventListener(mazeGameMessageHandler);

  // 儲存監聽器引用到外部變數
  messageHandlerRef = mazeGameMessageHandler;

  // 初始化渲染器
  renderer = new MazeRenderer(mazeCanvas.value);

  // 註冊鍵盤事件
  window.addEventListener('keydown', handleKeyPress);
  window.addEventListener('resize', updateScale);
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyPress);
  window.removeEventListener('resize', handleResize);
  // 移除頁面重新整理警告
  window.removeEventListener('beforeunload', handleBeforeUnload);
  // 移除 messageBridge 監聽器
  if (messageHandlerRef) {
    messageBridge.removeEventListener(messageHandlerRef);
    messageHandlerRef = null;
  }
  stopMonsterMovement(); // 清理計時器
  try {
    [readySound.value, goSound.value, gameOverSound.value, correctSound.value, wrongSound.value, backgroundMusic.value]
      .forEach(s => { if (s) { s.pause(); s.currentTime = 0; } });
  } catch (e) { void 0; }
});
</script>

<style scoped lang="less">
/* 導入貪食蛇基礎樣式 */
@import '@/assets/HTwbActKitCSS/HTsnakeGame.less';

/* 導入迷宮遊戲專屬樣式 */
@import '@/assets/HTwbActKitCSS/HTmazeGame.less';

.game-area.no-shadow {
  box-shadow: none !important;
  background: transparent !important;
}

/* 小螢幕警告樣式 */
.small-screen-warning {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.85);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  animation: slideDown 0.3s ease-out;

  .warning-content {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    gap: 12px;
    max-width: 1200px;
    margin: 0 auto;

    .warning-icon {
      width: 20px;
      height: 20px;
      color: white;
      flex-shrink: 0;
    }

    .warning-text {
      flex: 1;
      color: white;
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
    }

    .warning-close {
      width: 28px;
      height: 28px;
      border: none;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
      flex-shrink: 0;

      &:hover {
        background: rgba(255, 255, 255, 0.25);
      }

      svg {
        width: 14px;
        height: 14px;
        color: white;
      }
    }
  }
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
